import React, { useState, useEffect, useRef, useCallback } from 'react';
import CanvasManager from './core/CanvasManager';
import HistoryManager from './core/HistoryManager';
import GridManager from './core/GridManager';
import LayerManager from './core/LayerManager';
import ViewManager from './core/ViewManager';
import ImageFilters from './core/ImageFilters';
import BackgroundRemoval from './core/BackgroundRemoval';
import eventBus from './core/EventBus';
import Header from './components/Header';
import LeftToolbar from './components/Toolbar/LeftToolbar';
import TopToolbar from './components/Toolbar/TopToolbar';
import ProductPanel from './components/ProductPanel/ProductPanel';
// LayerPanel is now integrated into LeftToolbar
import ViewTabs from './components/Views/ViewTabs';
import ZoomControl from './components/Canvas/ZoomControl';
import CanvasArea from './components/Canvas/CanvasArea';
import PropertyPanel from './components/Toolbar/PropertyPanel';
import ToastContainer from './components/ToastContainer';
import AssistantPanel from './components/AssistantPanel';
import AssistantOverlay from './components/AssistantOverlay';
import OnboardingGuide from './components/OnboardingGuide';
import PreviewOverlay from './components/PreviewOverlay';
import CollageImageUploader from './components/CollageImageUploader';
import toast from './core/ToastManager';
import speech from './core/SpeechService';
import FontPicker from './components/FontPicker';
import WarningToast from './components/WarningToast';
import useIsMobile from './hooks/useIsMobile';
import useAutosave, { readAutosave, clearAutosave as clearAutosaveStorage } from './hooks/useAutosave';
import MobileTopBar from './components/Mobile/MobileTopBar';
import MobileBottomTabBar from './components/Mobile/MobileBottomTabBar';
import MobileBottomSheet from './components/Mobile/MobileBottomSheet';
import MobileShapesSheet from './components/Mobile/MobileShapesSheet';
import MobileTextSheet from './components/Mobile/MobileTextSheet';
import MobileImagesSheet from './components/Mobile/MobileImagesSheet';
import MobilePropertySheet from './components/Mobile/MobilePropertySheet';
import MobileLayersSheet from './components/Mobile/MobileLayersSheet';
import MobileMoreSheet from './components/Mobile/MobileMoreSheet';
import MobileSizeSheet from './components/Mobile/MobileSizeSheet';
import MyDesignsPanel from './components/MyDesignsPanel';
import { getSuggestions, detectContext, CATEGORY_LABELS, CATEGORY_ICONS } from './data/textSuggestions';
import { checkText, autoFixText } from './data/commonTypos';

// Whitelabel-safe API base: always resolved same-origin from the mount path.
// Shop domain /selenny-designer/ or /designer/ both map to <origin>/<segment>/backend/api.
function __computeApiBase() {
  const loc = window.location;
  const pathParts = loc.pathname.split('/');
  let idx = pathParts.indexOf('selenny-designer');
  if (idx < 0) idx = pathParts.indexOf('designer');
  if (idx >= 0) {
    return loc.origin + pathParts.slice(0, idx + 1).join('/') + '/backend/api';
  }
  return process.env.REACT_APP_API_URL || '/designer/api';
}
const API_BASE = __computeApiBase();
const PRODUCTS_API = API_BASE;

// Loading overlay fuer Embedded-Modus — laedt im Hintergrund Config, Fonts, TTS
// User muss klicken damit Browser Audio erlaubt (Autoplay-Policy)
function EmbeddedLoader({ onReady }) {
  const [progress, setProgress] = useState(0);
  const [ready, setReady] = useState(false);
  const isMobileLoader = window.innerWidth < 768;

  useEffect(() => {
    let cancelled = false;
    const interval = setInterval(() => {
      setProgress(p => {
        if (p >= 100) {
          clearInterval(interval);
          if (!cancelled) setReady(true);
          return 100;
        }
        return p + 20;
      });
    }, 80);
    return () => { cancelled = true; clearInterval(interval); };
  }, []);

  const handleStart = () => {
    speech.welcome();
    onReady();
  };

  return (
    <div className="fixed inset-0 z-[500] flex flex-col items-center justify-center bg-gray-900 px-6">
      <div className="w-16 h-16 mb-4 rounded-2xl bg-blue-600/20 flex items-center justify-center" style={{ animation: 'welcome-logo 0.6s cubic-bezier(0.16, 1, 0.3, 1) both' }}>
        <svg className="w-9 h-9 text-blue-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
          <rect x="3" y="3" width="18" height="18" rx="2" /><path d="M9 3v18M3 9h18" />
        </svg>
      </div>
      <p className="text-white/80 text-base font-semibold mb-1">Selenny® Designer</p>
      <p className="text-white/40 text-xs mb-5">Professioneller Druckdesigner</p>
      <div className="w-40 h-1 rounded-full bg-white/10 overflow-hidden mb-6">
        <div className="h-full bg-blue-500 rounded-full transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>
      {ready && (
        <>
          <button
            onClick={handleStart}
            className="px-8 py-3 bg-blue-600 text-white text-sm font-bold rounded-xl shadow-lg hover:bg-blue-500 transition-all hover:scale-105 active:scale-95"
            style={{ animation: 'welcome-title 0.5s cubic-bezier(0.16, 1, 0.3, 1) both' }}
          >
            Designer starten
          </button>
          {isMobileLoader && (
            <div className="mt-8 mx-4 p-4 rounded-xl text-center" style={{ animation: 'welcome-title 0.5s cubic-bezier(0.16, 1, 0.3, 1) 0.3s both', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.3)' }}>
              <div className="flex items-center justify-center gap-2 mb-2">
                <svg className="w-5 h-5 text-amber-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
                <span className="text-amber-400 text-sm font-bold">Hinweis</span>
              </div>
              <p className="text-white/70 text-sm leading-relaxed">
                Auf dem Smartphone ist die Gestaltung eingeschränkt. Für optimale Ergebnisse empfehlen wir ein <strong className="text-white/90">Tablet</strong> oder einen <strong className="text-white/90">Desktop-PC</strong>.
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// Welcome splash — user must click to start (needed for browser audio autoplay policy)
function WelcomeSplash({ onDone }) {
  const [phase, setPhase] = useState('enter'); // enter -> exit

  useEffect(() => {
    if (phase === 'exit') {
      const t = setTimeout(onDone, 600);
      return () => clearTimeout(t);
    }
  }, [phase, onDone]);

  const dismiss = () => {
    // User click = interaction → browser allows audio now
    speech.welcome();
    setPhase('exit');
  };

  return (
    <div
      className={`fixed inset-0 z-[500] flex items-center justify-center transition-opacity duration-500 ${phase === 'exit' ? 'opacity-0' : 'opacity-100'}`}
      style={{ background: 'radial-gradient(ellipse at center, rgba(37,99,235,0.95) 0%, rgba(67,56,202,0.97) 100%)' }}
    >
      {/* Animated background particles */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {[...Array(6)].map((_, i) => (
          <div
            key={i}
            className="absolute rounded-full bg-white/5"
            style={{
              width: 60 + i * 40,
              height: 60 + i * 40,
              left: `${15 + i * 13}%`,
              top: `${20 + (i % 3) * 25}%`,
              animation: `welcome-float ${4 + i}s ease-in-out infinite alternate`,
              animationDelay: `${i * 0.3}s`,
            }}
          />
        ))}
      </div>

      <div className="relative text-center">
        {/* Logo */}
        <div
          className="w-24 h-24 mx-auto mb-6 rounded-3xl flex items-center justify-center"
          style={{
            background: 'rgba(255,255,255,0.15)',
            backdropFilter: 'blur(10px)',
            animation: 'welcome-logo 0.8s cubic-bezier(0.16, 1, 0.3, 1) both',
          }}
        >
          <svg className="w-14 h-14 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="3" y="3" width="18" height="18" rx="2" />
            <path d="M9 3v18M3 9h18" />
          </svg>
        </div>

        {/* Title */}
        <h1
          className="text-5xl font-bold text-white mb-3 tracking-tight"
          style={{ animation: 'welcome-title 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.2s both' }}
        >
          Selenny® Designer
        </h1>

        {/* Subtitle */}
        <p
          className="text-white/60 text-lg mb-2"
          style={{ animation: 'welcome-title 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.4s both' }}
        >
          Professioneller Druckdesigner
        </p>
        <p
          className="text-white/40 text-sm mb-8 max-w-xs mx-auto"
          style={{ animation: 'welcome-title 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.5s both' }}
        >
          Der einzige Online-Designer mit echten 3D-Schriften für Banner, Klebefolien & Drucksachen
        </p>

        {/* Loading bar */}
        <div
          className="mx-auto w-48 h-1 rounded-full overflow-hidden bg-white/10"
          style={{ animation: 'welcome-title 0.6s cubic-bezier(0.16, 1, 0.3, 1) 0.6s both' }}
        >
          <div
            className="h-full bg-white/60 rounded-full"
            style={{ animation: 'welcome-bar 2.5s ease-in-out forwards' }}
          />
        </div>

        {/* Start button */}
        <div style={{ animation: 'welcome-title 0.8s cubic-bezier(0.16, 1, 0.3, 1) 0.8s both' }}>
          <button onClick={dismiss} className="mt-8 px-10 py-3.5 bg-white text-blue-600 text-lg font-bold rounded-2xl shadow-xl hover:shadow-2xl transition-all hover:scale-105 active:scale-95 animate-pulse-soft">
            Jetzt starten
          </button>
        </div>
      </div>
    </div>
  );
}

const FONTS = [
  'ABeeZee', 'Aclonica', 'Acme', 'Actor', 'Advent Pro', 'Akronim',
  'Alegreya', 'Alegreya Sans', 'Allura', 'Amatic SC', 'Amita', 'Anton',
  'Archivo Black', 'Arial', 'Bad Script', 'Bowlby One SC', 'Bungee Outline',
  'Bungee Shade', 'Comic Sans MS', 'Courier New', 'Dancing Script',
  'Expletus Sans', 'Fredoka One', 'Frijole', 'Georgia', 'Helvetica',
  'Homemade Apple', 'Impact', 'Imprima', 'Inter', 'Kaushan Script',
  'Libre Baskerville', 'Lobster', 'Luckiest Guy', 'Open Sans', 'Oranienbaum',
  'Orbitron', 'Oswald', 'Pacifico', 'Palatino', 'Palanquin', 'Palanquin Dark',
  'Parisienne', 'Paytone One', 'Permanent Marker', 'Pirata One', 'Plaster',
  'Poiret One', 'Prompt', 'Quintessential', 'Qwigley', 'Raleway',
  'Raleway Dots', 'Rammetto One', 'Ranchers', 'Ranga', 'Roboto', 'Rochester',
  'Rock Salt', 'Rubik Mono One', 'Russo One', 'Ruthie', 'Sacramento', 'Sail',
  'Sarina', 'Satisfy', 'Sedgwick Ave', 'Seymour One', 'Shadows Into Light',
  'Shadows Into Light Two', 'Shojumaru', 'Sirin Stencil', 'Sonsie One',
  'Spicy Rice', 'StjediseSans', 'Syncopate', 'Times New Roman', 'Titan One',
  'Trade Winds', 'Trebuchet MS', 'Ubuntu Condensed', 'UnifrakturMaguntia',
  'Varela Round', 'Vast Shadow', 'Verdana', 'Waiting for the Sunrise',
  'Wendy One', 'Wire One', 'Work Sans', 'Yanone Kaffeesatz', 'Yeseva One',
  'Yesteryear', 'Zeyada',
];

const TEXT_EFFECTS = [
  { id: 'none', label: 'Normal', icon: <svg viewBox="0 0 32 24"><text x="8" y="19" fontSize="18" fontFamily="sans-serif" fill="currentColor">T</text></svg> },
  { id: 'skew-right', label: 'Neigung rechts', icon: <svg viewBox="0 0 32 24"><text x="8" y="19" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="skewX(-12) translate(5,0)">T</text></svg> },
  { id: 'skew-left', label: 'Neigung links', icon: <svg viewBox="0 0 32 24"><text x="10" y="19" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="skewX(12) translate(-3,0)">T</text></svg> },
  { id: 'wide', label: 'Breit', icon: <svg viewBox="0 0 32 24"><text x="3" y="19" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="scale(1.4,1) translate(1,0)">T</text></svg> },
  { id: 'tall', label: 'Hoch', icon: <svg viewBox="0 0 32 24"><text x="10" y="21" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="scale(1,1.3) translate(0,-2)">T</text></svg> },
  { id: 'arc-top', label: 'Bogen oben', icon: <svg viewBox="0 0 32 24"><path d="M4 18 Q16 2 28 18" fill="none" stroke="currentColor" strokeWidth="2"/><text x="13" y="14" fontSize="8" fontFamily="sans-serif" fill="currentColor">T</text></svg> },
  { id: 'arc-bottom', label: 'Bogen unten', icon: <svg viewBox="0 0 32 24"><path d="M4 6 Q16 22 28 6" fill="none" stroke="currentColor" strokeWidth="2"/><text x="13" y="18" fontSize="8" fontFamily="sans-serif" fill="currentColor">T</text></svg> },
  { id: 'circle', label: 'Kreis', icon: <svg viewBox="0 0 32 24"><circle cx="16" cy="12" r="9" fill="none" stroke="currentColor" strokeWidth="1.5"/><text x="13" y="10" fontSize="6" fontFamily="sans-serif" fill="currentColor">T</text></svg> },
  { id: 'wave', label: 'Welle', icon: <svg viewBox="0 0 32 24"><path d="M2 12 Q8 4 16 12 Q24 20 30 12" fill="none" stroke="currentColor" strokeWidth="2"/></svg> },
  { id: 'perspective-left', label: 'Perspektive L', icon: <svg viewBox="0 0 32 24"><text x="8" y="18" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="skewY(8) translate(0,-3)">T</text></svg> },
  { id: 'perspective-right', label: 'Perspektive R', icon: <svg viewBox="0 0 32 24"><text x="8" y="18" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="skewY(-8) translate(0,2)">T</text></svg> },
  { id: 'squeeze', label: 'Schmal', icon: <svg viewBox="0 0 32 24"><text x="10" y="19" fontSize="18" fontFamily="sans-serif" fill="currentColor" transform="scale(0.6,1) translate(8,0)">T</text></svg> },
];

// Wiederverwendbare Textvorschlaege-Komponente
function TextSuggestionsPanel({ text, onSelect, style }) {
  const [expanded, setExpanded] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null);
  const productName = new URLSearchParams(window.location.search).get('product') || '';

  const { category: detectedCat, ageGroup } = detectContext(text, productName);
  const suggestions = getSuggestions(text, productName);
  const categories = Object.keys(CATEGORY_LABELS);

  // Fuer manuelle Kategorie-Auswahl: getSuggestions mit fake-text triggern
  const getManualSuggestions = (cat) => {
    const keywordMap = {
      geburtstag: 'geburtstag', hochzeit: 'hochzeit', jubilaeum: 'jubilaeum',
      firmen_event: 'firma', baby: 'baby', abschied: 'abschied',
      schulanfang: 'schulanfang', silvester: 'silvester', vatertag: 'vatertag', muttertag: 'muttertag',
    };
    return getSuggestions(keywordMap[cat] || cat, '');
  };

  const shownSuggestions = activeCategory ? getManualSuggestions(activeCategory) : suggestions;

  if (!expanded) {
    return (
      <button
        onClick={() => setExpanded(true)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 rounded-lg transition-colors border border-dashed border-blue-300 dark:border-blue-700"
        style={style}
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <span className="font-medium">Textvorschlaege</span>
        <span className="text-gray-400 dark:text-gray-500 font-normal">\u2014 Passende Texte fuer deinen Anlass</span>
        <svg className="w-3.5 h-3.5 ml-auto" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>
    );
  }

  return (
    <div className="border border-blue-200 dark:border-blue-800 rounded-lg overflow-hidden" style={style}>
      {/* Header */}
      <button
        onClick={() => setExpanded(false)}
        className="w-full flex items-center gap-2 px-3 py-2 text-xs font-medium text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-900/30 hover:bg-blue-100 dark:hover:bg-blue-900/50 transition-colors"
      >
        <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>
        <span>Textvorschlaege</span>
        {ageGroup && <span className="bg-blue-600 text-white px-1.5 py-0.5 rounded text-[9px] font-bold">{ageGroup}. Geburtstag</span>}
        <svg className="w-3.5 h-3.5 ml-auto transform rotate-180" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <path d="M19 9l-7 7-7-7" />
        </svg>
      </button>

      {/* Kategorie-Chips */}
      <div className="flex gap-1.5 px-3 py-2 overflow-x-auto border-b border-blue-100 dark:border-blue-800" style={{ scrollbarWidth: 'none' }}>
        {categories.map((cat) => (
          <button
            key={cat}
            onClick={() => setActiveCategory(activeCategory === cat ? null : cat)}
            className={`flex-shrink-0 px-2.5 py-1 rounded-full text-[10px] font-medium transition-colors whitespace-nowrap ${
              (activeCategory || detectedCat) === cat
                ? 'bg-blue-600 text-white'
                : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'
            }`}
          >
            {CATEGORY_ICONS[cat]} {CATEGORY_LABELS[cat]}
          </button>
        ))}
      </div>

      {/* Vorschlaege als Pills */}
      <div className="p-3 max-h-[180px] overflow-y-auto flex flex-wrap gap-1.5">
        {shownSuggestions.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500">Keine Vorschlaege fuer diese Kategorie.</p>
        )}
        {shownSuggestions.map((suggestion, idx) => (
          <button
            key={idx}
            onClick={() => onSelect(suggestion)}
            className="px-3 py-1.5 text-xs rounded-full border border-gray-200 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 hover:text-blue-700 dark:hover:text-blue-300 transition-all cursor-pointer"
            title={suggestion}
          >
            {suggestion}
          </button>
        ))}
      </div>
      {/* Neu beginnen */}
      <div className="px-3 pb-2 flex gap-2">
        <button
          onClick={() => { onSelect(''); setActiveCategory(null); }}
          className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-medium rounded-lg bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors"
        >
          <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
          Neu beginnen
        </button>
      </div>
    </div>
  );
}

function TextEditModal({ obj, canvasManager, initialText, initialColor, initialFont, initialSize, isCurved, onClose }) {
  const [text, setText] = useState(initialText);
  const [color, setColor] = useState(initialColor);
  const [font, setFont] = useState(initialFont);
  const [size, setSize] = useState(initialSize);
  const [strokeColor, setStrokeColor] = useState(isCurved ? (obj?._curvedStroke || '') : (obj?.stroke || ''));
  const [strokeWidth, setStrokeWidth] = useState(isCurved ? (obj?._curvedStrokeWidth || 0) : (obj?.strokeWidth || 0));
  const [tab, setTab] = useState('text'); // 'text' | 'effects'
  const [typoWarnings, setTypoWarnings] = useState([]);
  const [showTypoBanner, setShowTypoBanner] = useState(false);

  // Shadow state — read from existing object
  const existingShadow = obj?.shadow;
  const [shadowEnabled, setShadowEnabled] = useState(!!existingShadow);
  const [shadowColor, setShadowColor] = useState(existingShadow?.color || 'rgba(0,0,0,0.5)');
  const [shadowOffsetX, setShadowOffsetX] = useState(existingShadow?.offsetX || 3);
  const [shadowOffsetY, setShadowOffsetY] = useState(existingShadow?.offsetY || 3);
  const [shadowBlur, setShadowBlur] = useState(existingShadow?.blur || 5);

  // Letter spacing & line height — read from existing object
  const [charSpacing, setCharSpacing] = useState(obj?.charSpacing || 0);
  const [lineHeight, setLineHeight] = useState(obj?.lineHeight || 1.16);

  // Originalzustand beim Öffnen merken — für Abbrechen/Restore
  const [originalState] = useState(() => {
    if (!obj) return null;
    if (isCurved) return { text: obj._curvedText, fill: obj._curvedFill, fontFamily: obj._curvedFontFamily, fontSize: obj._curvedFontSize, stroke: obj._curvedStroke, strokeWidth: obj._curvedStrokeWidth };
    return { text: obj.text, fill: obj.fill, fontFamily: obj.fontFamily, fontSize: obj.fontSize, stroke: obj.stroke || '', strokeWidth: obj.strokeWidth || 0, shadow: obj.shadow ? { ...obj.shadow } : null, charSpacing: obj.charSpacing || 0, lineHeight: obj.lineHeight || 1.16 };
  });

  const restoreOriginal = () => {
    if (!obj || !originalState || isCurved) { onClose(); return; }
    obj.set('text', originalState.text);
    obj.set('fill', originalState.fill);
    obj.set('fontFamily', originalState.fontFamily);
    obj.set('fontSize', originalState.fontSize);
    obj.set('stroke', originalState.stroke);
    obj.set('strokeWidth', originalState.strokeWidth);
    obj.set('charSpacing', originalState.charSpacing);
    obj.set('lineHeight', originalState.lineHeight);
    if (originalState.shadow) {
      obj.set('shadow', originalState.shadow);
    } else {
      obj.set('shadow', null);
    }
    if (obj._clearCache) obj._clearCache();
    obj.dirty = true;
    if (obj.initDimensions) obj.initDimensions();
    obj.setCoords();
    canvasManager?.canvas?.renderAll();
    onClose();
  };

  // Live preview helper — applies charSpacing/lineHeight directly to canvas object
  const applyLiveSpacing = (prop, value) => {
    if (!obj || isCurved) return;
    obj.set(prop, value);
    if (obj._clearCache) obj._clearCache();
    obj.dirty = true;
    if (obj.initDimensions) obj.initDimensions();
    obj.setCoords();
    canvasManager?.canvas?.renderAll();
  };

  const doApply = (finalText) => {
    const props = { fill: color, fontFamily: font, fontSize: size };
    if (strokeWidth > 0 && strokeColor) {
      props.stroke = strokeColor;
      props.strokeWidth = strokeWidth;
    } else {
      props.stroke = '';
      props.strokeWidth = 0;
    }
    // Shadow
    if (shadowEnabled) {
      props.shadow = { offsetX: shadowOffsetX, offsetY: shadowOffsetY, blur: shadowBlur, color: shadowColor };
    } else {
      props.shadow = null;
    }
    // Letter spacing & line height
    props.charSpacing = charSpacing;
    props.lineHeight = lineHeight;

    if (isCurved) {
      canvasManager?.updateCurvedText(obj, finalText, props);
    } else {
      canvasManager?.updateText(obj, finalText, props);
    }
    onClose();
  };

  const applyAndClose = () => {
    const warnings = checkText(text);
    if (warnings.length > 0) {
      setTypoWarnings(warnings);
      setShowTypoBanner(true);
    } else {
      setShowTypoBanner(false);
      doApply(text);
    }
  };

  const handleAutoFix = () => {
    const fixed = autoFixText(text, typoWarnings);
    setText(fixed);
    setShowTypoBanner(false);
    setTypoWarnings([]);
    doApply(fixed);
  };

  const handleIgnoreTypos = () => {
    setShowTypoBanner(false);
    setTypoWarnings([]);
    doApply(text);
  };

  const applyEffect = (effectId) => {
    canvasManager?.applyTextEffect(effectId);
  };

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/30 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget) restoreOriginal(); }}
    >
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-[calc(100%-2rem)] sm:w-[480px] max-h-[85vh] flex flex-col animate-popIn">
        {/* Header */}
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <p className="text-sm font-semibold text-gray-800 dark:text-gray-100">Text bearbeiten</p>
          <button onClick={restoreOriginal} className="w-7 h-7 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full text-lg">&times;</button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-gray-200 dark:border-gray-700 px-5 gap-0">
          {[{ key: 'text', label: 'Text & Stil' }, { key: 'effects', label: 'Effekte & Form' }].map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-xs font-medium border-b-2 transition-colors ${tab === t.key ? 'border-blue-600 text-blue-600' : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'}`}
            >{t.label}</button>
          ))}
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {tab === 'text' && (
            <>
              <textarea autoFocus value={text} onChange={(e) => { setText(e.target.value); setShowTypoBanner(false); }}
                className="selenny-input text-sm mb-3" rows={3}
                spellCheck={true} lang="de"
                onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) applyAndClose(); }}
              />

              {/* Textvorschlaege */}
              <TextSuggestionsPanel text={text} onSelect={(s) => setText(s)} style={{ marginBottom: '12px' }} />

              {/* Font, Size, Color row */}
              <div className="flex gap-2 mb-3">
                <div className="flex-1">
                  <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Schriftart</label>
                  <FontPicker value={font} onChange={(f) => setFont(f)} />
                </div>
                <div className="w-20">
                  <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Größe</label>
                  <input type="number" value={size} onChange={(e) => setSize(Number(e.target.value))} className="selenny-input text-xs" min="8" max="999" />
                </div>
                <div className="w-14">
                  <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Farbe</label>
                  <input type="color" value={color} onChange={(e) => setColor(e.target.value)} className="w-full h-[34px] cursor-pointer rounded border border-gray-300 dark:border-gray-600" />
                </div>
              </div>

              {/* Stroke / Outline */}
              <div className="mb-3 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Umrandung / Outline</p>
                <div className="flex gap-2 items-end">
                  <div className="flex-1">
                    <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Stärke</label>
                    <div className="flex gap-1">
                      {[0, 2, 5, 10, 20, 40].map((w) => (
                        <button key={w} onClick={() => setStrokeWidth(w)}
                          className={`flex-1 text-[10px] py-1 rounded border transition-colors ${strokeWidth === w ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 font-semibold' : 'border-gray-200 dark:border-gray-600 text-gray-500 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-500'}`}
                        >{w === 0 ? 'Keine' : `${w}px`}</button>
                      ))}
                    </div>
                  </div>
                  <div className="w-14">
                    <label className="text-[10px] text-gray-500 dark:text-gray-400 mb-0.5 block">Farbe</label>
                    <input type="color" value={strokeColor || '#000000'} onChange={(e) => { setStrokeColor(e.target.value); if (strokeWidth === 0) setStrokeWidth(2); }}
                      className="w-full h-[34px] cursor-pointer rounded border border-gray-300 dark:border-gray-600" />
                  </div>
                </div>
                <div className="flex gap-2 items-center mt-2">
                  <label className="text-[10px] text-gray-500 dark:text-gray-400 flex-shrink-0">Eigener Wert</label>
                  <input type="number" min="0" max="200" step="1" value={strokeWidth}
                    onChange={(e) => { const v = Math.max(0, Math.min(200, Number(e.target.value) || 0)); setStrokeWidth(v); }}
                    className="selenny-input w-20 text-[11px] py-1 px-2" />
                  <span className="text-[10px] text-gray-400 dark:text-gray-500">px (0–200)</span>
                  <input type="range" min="0" max="80" step="1" value={Math.min(strokeWidth, 80)}
                    onChange={(e) => setStrokeWidth(Number(e.target.value))}
                    className="flex-1" />
                </div>
                {strokeWidth > 0 && (
                  <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center min-h-[40px]">
                    <span style={{ fontFamily: font, fontSize: Math.min(size * 0.4, 28), color: color, WebkitTextStroke: `${strokeWidth}px ${strokeColor}` }}>
                      {text.substring(0, 20) || 'Vorschau'}
                    </span>
                  </div>
                )}
              </div>
            </>
          )}

          {tab === 'effects' && (
            <>
              <button onClick={() => { applyEffect('none'); setShadowEnabled(false); setCharSpacing(0); setLineHeight(1.16); applyLiveSpacing('charSpacing', 0); applyLiveSpacing('lineHeight', 1.16); if(obj && !isCurved) { obj.set('shadow', null); canvasManager?.canvas?.renderAll(); } }} className="w-full flex items-center justify-center gap-2 py-2 px-3 mb-3 rounded-lg text-xs font-medium bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 hover:bg-red-100 dark:hover:bg-red-900/40 transition-colors">
                <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 12a9 9 0 019-9 9.75 9.75 0 016.74 2.74L21 8"/><path d="M21 3v5h-5"/><path d="M21 12a9 9 0 01-9 9 9.75 9.75 0 01-6.74-2.74L3 16"/><path d="M3 21v-5h5"/></svg>
                Alle Effekte zurücksetzen
              </button>
              <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">Effekt auf den Text anwenden:</p>
              <div className="grid grid-cols-4 gap-2 mb-4">
                {TEXT_EFFECTS.map((effect) => (
                  <button key={effect.id} onClick={() => applyEffect(effect.id)}
                    className="flex flex-col items-center gap-1 p-2.5 rounded-lg border border-gray-200 dark:border-gray-600 hover:border-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-all text-gray-600 dark:text-gray-300 hover:text-blue-600 dark:hover:text-blue-400"
                  >
                    <div className="w-8 h-6">{effect.icon}</div>
                    <span className="text-[8px] text-gray-500 dark:text-gray-400 leading-tight text-center">{effect.label}</span>
                  </button>
                ))}
              </div>

              {/* Shadow Editor */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Schatten</p>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input type="checkbox" checked={shadowEnabled} onChange={(e) => setShadowEnabled(e.target.checked)} className="sr-only peer" />
                    <div className="w-8 h-4 bg-gray-300 dark:bg-gray-600 peer-checked:bg-blue-600 rounded-full transition-colors after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-3 after:w-3 after:transition-all peer-checked:after:translate-x-4"></div>
                  </label>
                </div>
                {shadowEnabled && (
                  <div className="space-y-2">
                    <div className="flex gap-2 items-center">
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 w-16 flex-shrink-0">Farbe</label>
                      <input type="color" value={shadowColor.startsWith('rgba') ? '#000000' : shadowColor} onChange={(e) => setShadowColor(e.target.value)}
                        className="w-8 h-8 cursor-pointer rounded border border-gray-300 dark:border-gray-600 p-0" />
                      <input type="text" value={shadowColor} onChange={(e) => setShadowColor(e.target.value)}
                        className="selenny-input text-[10px] flex-1 font-mono" placeholder="rgba(0,0,0,0.5)" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between">
                        <span>X-Versatz</span><span className="font-mono">{shadowOffsetX}px</span>
                      </label>
                      <input type="range" min="-20" max="20" step="1" value={shadowOffsetX}
                        onChange={(e) => setShadowOffsetX(Number(e.target.value))}
                        className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between">
                        <span>Y-Versatz</span><span className="font-mono">{shadowOffsetY}px</span>
                      </label>
                      <input type="range" min="-20" max="20" step="1" value={shadowOffsetY}
                        onChange={(e) => setShadowOffsetY(Number(e.target.value))}
                        className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                    </div>
                    <div>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between">
                        <span>Weichzeichnung</span><span className="font-mono">{shadowBlur}px</span>
                      </label>
                      <input type="range" min="0" max="30" step="1" value={shadowBlur}
                        onChange={(e) => setShadowBlur(Number(e.target.value))}
                        className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                    </div>
                    {/* Shadow preview */}
                    <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center min-h-[40px]">
                      <span style={{
                        fontFamily: font, fontSize: Math.min(size * 0.4, 28), color: color,
                        textShadow: `${shadowOffsetX}px ${shadowOffsetY}px ${shadowBlur}px ${shadowColor}`
                      }}>
                        {text.substring(0, 20) || 'Vorschau'}
                      </span>
                    </div>
                  </div>
                )}
              </div>

              {/* Letter Spacing */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Buchstabenabstand</p>
                <div>
                  <label className="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between">
                    <span>Abstand</span><span className="font-mono">{charSpacing}</span>
                  </label>
                  <input type="range" min="-200" max="800" step="10" value={charSpacing}
                    onChange={(e) => { const v = Number(e.target.value); setCharSpacing(v); applyLiveSpacing('charSpacing', v); }}
                    className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                </div>
                {charSpacing !== 0 && (
                  <div className="mt-2 p-2 bg-white dark:bg-gray-900 rounded border border-gray-200 dark:border-gray-600 flex items-center justify-center min-h-[32px]">
                    <span style={{
                      fontFamily: font, fontSize: Math.min(size * 0.35, 22), color: color,
                      letterSpacing: `${charSpacing / 100}em`
                    }}>
                      {text.substring(0, 20) || 'Vorschau'}
                    </span>
                  </div>
                )}
              </div>

              {/* Line Height */}
              <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-700 rounded-lg border border-gray-100 dark:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase mb-2">Zeilenabstand</p>
                <div>
                  <label className="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between">
                    <span>Höhe</span><span className="font-mono">{lineHeight.toFixed(2)}</span>
                  </label>
                  <input type="range" min="0.5" max="3.0" step="0.05" value={lineHeight}
                    onChange={(e) => { const v = Number(e.target.value); setLineHeight(v); applyLiveSpacing('lineHeight', v); }}
                    className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600" />
                </div>
              </div>

              {/* Manual sliders */}
              <div className="space-y-3 pt-3 border-t border-gray-200 dark:border-gray-600">
                <p className="text-[10px] font-semibold text-gray-500 dark:text-gray-400 uppercase">Manuelle Einstellungen</p>
                {[
                  { label: 'Neigung X', prop: 'skewX', min: -45, max: 45, unit: '°' },
                  { label: 'Neigung Y', prop: 'skewY', min: -45, max: 45, unit: '°' },
                  { label: 'Breite', prop: 'scaleX', min: 20, max: 300, unit: '%', scale: 100 },
                  { label: 'Höhe', prop: 'scaleY', min: 20, max: 300, unit: '%', scale: 100 },
                ].map((s) => {
                  const rawVal = s.scale ? (obj?.[s.prop] || 1) * s.scale : (obj?.[s.prop] || 0);
                  return (
                    <div key={s.prop}>
                      <label className="text-[10px] text-gray-500 dark:text-gray-400 flex justify-between">
                        <span>{s.label}</span>
                        <span className="font-mono">{Math.round(rawVal)}{s.unit}</span>
                      </label>
                      <input type="range" min={s.min} max={s.max} step="1" value={Math.round(rawVal)}
                        onChange={(e) => {
                          const v = s.scale ? Number(e.target.value) / s.scale : Number(e.target.value);
                          obj.set(s.prop, v);
                          obj.setCoords();
                          canvasManager?.canvas?.renderAll();
                        }}
                        className="w-full h-1.5 bg-gray-200 dark:bg-gray-600 rounded-lg appearance-none cursor-pointer accent-blue-600"
                      />
                    </div>
                  );
                })}
              </div>
            </>
          )}
        </div>

        {/* Typo Warning Banner */}
        {showTypoBanner && typoWarnings.length > 0 && (
          <div className="mx-5 mb-1 p-3 bg-yellow-50 dark:bg-yellow-900/30 border border-yellow-300 dark:border-yellow-700 rounded-lg">
            <div className="flex items-start gap-2">
              <svg className="w-4 h-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0 mt-0.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M12 9v4M12 17h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z" />
              </svg>
              <div className="flex-1 min-w-0">
                <p className="text-xs font-semibold text-yellow-800 dark:text-yellow-200 mb-1">Mögliche Tippfehler gefunden:</p>
                <div className="flex flex-wrap gap-1">
                  {typoWarnings.map((w, i) => (
                    <span key={i} className="text-xs text-yellow-700 dark:text-yellow-300">
                      <span className="line-through opacity-60">{w.word}</span>
                      <span className="mx-0.5">&rarr;</span>
                      <span className="font-semibold">{w.suggestion}</span>
                      {i < typoWarnings.length - 1 && <span className="ml-1 text-yellow-400">|</span>}
                    </span>
                  ))}
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-2">
              <button onClick={handleAutoFix}
                className="flex-1 text-xs font-medium px-3 py-1.5 rounded-md bg-yellow-600 text-white hover:bg-yellow-700 transition-colors">
                Automatisch korrigieren
              </button>
              <button onClick={handleIgnoreTypos}
                className="flex-1 text-xs font-medium px-3 py-1.5 rounded-md border border-yellow-400 dark:border-yellow-600 text-yellow-700 dark:text-yellow-300 hover:bg-yellow-100 dark:hover:bg-yellow-900/50 transition-colors">
                Ignorieren
              </button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="px-5 pb-4 pt-2 border-t border-gray-100 dark:border-gray-700 flex gap-2">
          <button onClick={applyAndClose} className="selenny-btn-primary flex-1 text-sm">Übernehmen</button>
          <button onClick={restoreOriginal} className="selenny-btn-secondary flex-1 text-sm">Abbrechen</button>
        </div>
        <p className="text-[10px] text-gray-400 dark:text-gray-500 pb-3 text-center hidden sm:block">Cmd+Enter zum schnellen Übernehmen</p>
      </div>
    </div>
  );
}

function ContextHints({ activeObject, canvasManager }) {
  const isText = activeObject && ['i-text', 'text', 'textbox'].includes(activeObject.type);
  const isImage = activeObject && activeObject.type === 'image';
  const isGroup = activeObject && activeObject.type === 'group';
  const isShape = activeObject && !isText && !isImage && !isGroup;

  const hints = [];

  if (!activeObject) {
    return null;
  } else if (isText) {
    hints.push(
      { icon: 'edit', text: 'Doppelklick: Text bearbeiten' },
      { icon: 'move', text: 'Ziehen: Verschieben' },
      { icon: 'copy', text: 'Strg+D: Duplizieren' },
      { icon: 'delete', text: 'Entf: Löschen' },
    );
  } else if (isImage) {
    hints.push(
      { icon: 'move', text: 'Ziehen: Verschieben' },
      { icon: 'scale', text: 'Ecken: Skalieren' },
      { icon: 'copy', text: 'Strg+D: Duplizieren' },
      { icon: 'delete', text: 'Entf: Löschen' },
    );
  } else if (isGroup) {
    hints.push(
      { icon: 'ungroup', text: 'Strg+Shift+G: Gruppierung auflösen' },
      { icon: 'move', text: 'Ziehen: Verschieben' },
      { icon: 'delete', text: 'Entf: Löschen' },
    );
  } else if (isShape) {
    hints.push(
      { icon: 'move', text: 'Ziehen: Verschieben' },
      { icon: 'scale', text: 'Ecken: Skalieren' },
      { icon: 'rotate', text: 'Oben rechts: Drehen' },
      { icon: 'copy', text: 'Strg+D: Duplizieren' },
    );
  }

  const iconMap = {
    shape: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="3" width="8" height="8" rx="1"/><circle cx="17" cy="17" r="4"/></svg>,
    mouse: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2v6M12 22v-6M2 12h6M22 12h-6"/><circle cx="12" cy="12" r="2"/></svg>,
    zoom: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7"/><path d="M21 21l-4-4"/><path d="M8 11h6M11 8v6"/></svg>,
    edit: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17 3a2.83 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/></svg>,
    move: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M5 9l-3 3 3 3M9 5l3-3 3 3M15 19l-3 3-3-3M19 9l3 3-3 3M2 12h20M12 2v20"/></svg>,
    copy: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="8" y="8" width="12" height="12" rx="1"/><path d="M4 16V5a1 1 0 0 1 1-1h11"/></svg>,
    delete: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 6h18M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6"/></svg>,
    scale: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M15 3h6v6M9 21H3v-6M21 3l-7 7M3 21l7-7"/></svg>,
    rotate: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 12a9 9 0 1 1-9-9"/><path d="M21 3v6h-6"/></svg>,
    ungroup: <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="2" y="2" width="8" height="8" rx="1"/><rect x="14" y="14" width="8" height="8" rx="1"/></svg>,
  };

  return (
    <div className="absolute top-2 left-1/2 -translate-x-1/2 z-30 flex items-center gap-3 bg-white/95 backdrop-blur-sm rounded-full px-4 py-1.5 shadow-md border border-gray-200/60 pointer-events-none animate-fadeIn">
      {hints.map((h, i) => (
        <span key={i} className="flex items-center gap-1.5 text-[10px] text-gray-500 whitespace-nowrap">
          <span className="text-gray-400">{iconMap[h.icon]}</span>
          {h.text}
        </span>
      ))}
      <span className="text-[10px] text-blue-500 font-medium whitespace-nowrap ml-1">
        {isText ? 'Text' : isImage ? 'Bild' : isGroup ? 'Gruppe' : activeObject.type === 'rect' ? 'Rechteck' : activeObject.type === 'circle' ? 'Kreis' : 'Objekt'}
      </span>
    </div>
  );
}

function App() {
  const canvasManagerRef = useRef(null);
  const historyManagerRef = useRef(null);
  const gridManagerRef = useRef(null);
  const layerManagerRef = useRef(null);
  const viewManagerRef = useRef(null);
  const imageFiltersRef = useRef(null);
  const bgRemovalRef = useRef(null);
  const handleAddToCartRef = useRef(null);
  const pricingRef = useRef(null);

  const [, forceUpdate] = useState(0);
  const [isInitialized, setIsInitialized] = useState(false);
  const [activeObject, setActiveObject] = useState(null);
  const [layers, setLayers] = useState([]);
  const [views, setViews] = useState([]);
  const [activeViewIndex, setActiveViewIndex] = useState(0);
  const [zoom, setZoom] = useState(0.5);
  const [gridVisible, setGridVisible] = useState(false);
  const [canUndo, setCanUndo] = useState(false);
  const [canRedo, setCanRedo] = useState(false);
  const [previewMode, setPreviewMode] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  // Layer panel is now in LeftToolbar
  const isEmbedded = new URLSearchParams(window.location.search).get('embedded') === '1';
  const hideSizes = new URLSearchParams(window.location.search).get('hide_sizes') === '1';
  const [customerEmail, setCustomerEmail] = useState(
    () => new URLSearchParams(window.location.search).get('customer_email') || ''
  );
  const [customerName, setCustomerName] = useState(
    () => new URLSearchParams(window.location.search).get('customer_name') || ''
  );
  const orderNumber = new URLSearchParams(window.location.search).get('order_number') || new URLSearchParams(window.location.search).get('ordernumber') || '';
  const shopType = new URLSearchParams(window.location.search).get('shop_type') || 'manual';
  const isCustomerDesign = isEmbedded || !!customerEmail;
  const isLoggedIn = !!customerEmail;

  // Same-origin detection of logged-in shop customer (WP/Shopware).
  // Falls kein customer_email URL-Param gesetzt ist, versucht der Designer die Info
  // vom Shop-System zu bekommen. Whitelabel-safe: nur same-origin-Endpoints.
  useEffect(() => {
    if (customerEmail) return;
    let alive = true;
    const endpoints = [
      '/wp-admin/admin-ajax.php?action=selenny_customer_info',  // WooCommerce
      '/SelennyDesigner/customerInfo',                          // Shopware 5
    ];
    (async () => {
      for (const url of endpoints) {
        try {
          const r = await fetch(url, { credentials: 'same-origin' });
          if (!r.ok) continue; // 404 => wrong shop system, try next silently
          const data = await r.json().catch(() => null);
          if (!data || typeof data.loggedIn === 'undefined') continue;
          // Any endpoint answering with a structured response owns the decision.
          if (alive && data.loggedIn && data.email) {
            setCustomerEmail(data.email);
            if (data.name) setCustomerName(data.name);
          }
          return; // stop after first answering endpoint
        } catch {}
      }
    })();
    return () => { alive = false; };
  }, [customerEmail]);
  // Admin-Modus: nur nach erfolgreichem Login
  const [isAdmin, setIsAdmin] = useState(() => {
    if (isEmbedded) return false;
    // Admin via URL params (wenn aus Admin-Panel per Iframe/Tab gestartet)
    const sp = new URLSearchParams(window.location.search);
    const urlUser = sp.get('_user');
    const urlPass = sp.get('_pass');
    if (urlUser && urlPass) {
      try {
        sessionStorage.setItem('selenny-admin', '1');
        sessionStorage.setItem('selenny-admin-user', urlUser);
        sessionStorage.setItem('selenny-admin-pass', urlPass);
      } catch {}
      return true;
    }
    return !!sessionStorage.getItem('selenny-admin');
  });
  const [showAdminLogin, setShowAdminLogin] = useState(false);

  // Admin-Login prüfen
  const handleAdminLogin = async (user, pass) => {
    try {
      const baseUrl = window.location.pathname.replace(/\/index\.html$/, '').replace(/\/$/, '');
      const resp = await fetch(`${baseUrl}/backend/auth-check.php?_user=${encodeURIComponent(user)}&_pass=${encodeURIComponent(pass)}`);
      if (resp.ok) {
        sessionStorage.setItem('selenny-admin', '1');
        sessionStorage.setItem('selenny-admin-user', user);
        sessionStorage.setItem('selenny-admin-pass', pass);
        setIsAdmin(true);
        setShowAdminLogin(false);
        return true;
      }
    } catch {}
    return false;
  };
  const [showWelcome, setShowWelcome] = useState(!isEmbedded);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [speechEnabled, setSpeechEnabled] = useState(speech.enabled);
  const [darkMode, setDarkMode] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('dark') === '1') return true;
    if (params.get('dark') === '0') return false;
    return localStorage.getItem('selenny-dark') === 'true';
  });
  const [showRulers, setShowRulers] = useState(true);

  // --- Design Sharing ---
  const [isSharedView, setIsSharedView] = useState(false);
  const [sharedDesignName, setSharedDesignName] = useState('');
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareUrl, setShareUrl] = useState('');

  const [editingTextObj, setEditingTextObj] = useState(null);
  const [editingTextValue, setEditingTextValue] = useState('');
  const [editingTextColor, setEditingTextColor] = useState('#000000');
  const [editingTextFont, setEditingTextFont] = useState('Inter');
  const [editingTextSize, setEditingTextSize] = useState(24);
  const [editingTextIsCurved, setEditingTextIsCurved] = useState(false);
  // Copy-as-Template Modal (replaces window.prompt which is blocked in iframes)
  const [showCopyTemplateModal, setShowCopyTemplateModal] = useState(false);
  const [copyTemplateName, setCopyTemplateName] = useState('');
  const [copyTemplateCategory, setCopyTemplateCategory] = useState('');
  const [copyTemplateLoading, setCopyTemplateLoading] = useState(false);
  // Sprachausgabe-Hinweis (zeigt einmalig, dass Ton aus ist)
  const [showSpeechHint, setShowSpeechHint] = useState(false);
  const [activeCollage, setActiveCollage] = useState(null); // { collageId, placeholderCount }
  const [collageMinimized, setCollageMinimized] = useState(false);
  const [hasBgImage, setHasBgImage] = useState(false);
  const [whatsappNumber, setWhatsappNumber] = useState(null);
  const [whatsappText, setWhatsappText] = useState('Hallo, ich habe eine Frage zu meinem Design.');

  const isMobile = useIsMobile();
  const [mobileTab, setMobileTab] = useState(null);

  // Embedded: Loading-Screen anzeigen
  const [embeddedLoading, setEmbeddedLoading] = useState(isEmbedded);

  // Design/Template loading overlay
  const hasDesignParam = !!(new URLSearchParams(window.location.search).get('design_id') || new URLSearchParams(window.location.search).get('template_id'));
  const [designLoading, setDesignLoading] = useState(hasDesignParam);
  const [designLoadProgress, setDesignLoadProgress] = useState(0);
  const [designLoadStatus, setDesignLoadStatus] = useState('Design wird geladen...');

  // Begruessung kommt direkt vom EmbeddedLoader/WelcomeSplash Button-Klick

  // Browser-Zurück-Button abfangen → schwarzen Bildschirm verhindern
  const mobileTabRef = useRef(mobileTab);
  useEffect(() => { mobileTabRef.current = mobileTab; }, [mobileTab]);

  useEffect(() => {
    // Im embedded-Modus: KEINE History-Manipulation und KEIN popstate im iframe
    // Der Parent (Shop) regelt History + Close komplett. Popstate im iframe
    // fuehrt auf Mobile zu einer Navigationsschleife (Parent popstate + iframe popstate
    // feuern gleichzeitig → doppeltes Close → history.back() navigiert weg).
    if (isEmbedded) {
      return; // Nichts tun — Parent handled alles
    }
    // Nicht-embedded: eigene History
    window.history.pushState({ designer: true }, '');
    const handlePopState = () => {
      if (mobileTabRef.current) {
        window.history.pushState({ designer: true }, '');
        setMobileTab(null);
      } else {
        window.history.pushState({ designer: true }, '');
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isEmbedded]);

  const [designId, setDesignId] = useState(null);
  const [expiresInDays, setExpiresInDays] = useState(null);
  const [showExpiryWarning, setShowExpiryWarning] = useState(false);
  // Recovery-Dialog (Autosave): { data, dbSavedAt } oder null
  const [recoveryData, setRecoveryData] = useState(null);
  // Verhindert dass das Recovery-Modal beim Seiten-Wechsel mehrmals geprüft wird
  const recoveryCheckedRef = useRef(false);
  const [widthCm, setWidthCm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const w = parseFloat(params.get('width'));
    return w > 0 ? w : 100;
  });
  const [heightCm, setHeightCm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const h = parseFloat(params.get('height'));
    return h > 0 ? h : 50;
  });
  // Track if we're waiting for API dimensions (prevents canvas init with wrong defaults)
  const [awaitingApiDims, setAwaitingApiDims] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const hasOrderNum = !!(params.get('order_number') || params.get('ordernumber'));
    const hasExplicitDims = parseFloat(params.get('width')) > 0 && parseFloat(params.get('height')) > 0;
    // If we have an order_number but no explicit dimensions, we need to wait for API
    return hasOrderNum && !hasExplicitDims;
  });
  const [productName, setProductName] = useState('');
  const [productId, setProductId] = useState(null);
  // Pro Produkt erlaubte Clipart-Kategorien (leer = alle anzeigen). Spart Ladezeit/Auswahl.
  const [allowedClipartCategories, setAllowedClipartCategories] = useState([]);
  const [shopUrl, setShopUrl] = useState('');
  const [urlPricing, setUrlPricing] = useState(null); // URL-based pricing for WooCommerce etc.
  const [bleedCm, setBleedCm] = useState(1);
  const [printDpi, setPrintDpi] = useState(null);
  const [hemCm, setHemCm] = useState(0);
  const [hemPerSide, setHemPerSide] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
  const [grommets, setGrommets] = useState({ enabled: false, mode: 'corners', customSpacingCm: 40, radiusMm: 11 });
  const [hohlsaum, setHohlsaum] = useState({ top: 0, bottom: 0, left: 0, right: 0 });
  const [overlayColors, setOverlayColors] = useState({ bleedColor: '#ef4444', hemColor: '#3b82f6' });

  // Per-side finishing config for backend calculation
  const defaultSide = { type: 'cut', saumCm: 3, grommetMode: 'every50', grommetSize: 11, hohlsaumCm: 5, kederType: 'round-white', klettPos: 'front' };
  const [finishing, setFinishing] = useState({
    mode: 'all', // 'all' or 'perSide'
    all: { ...defaultSide },
    top: { ...defaultSide },
    bottom: { ...defaultSide },
    left: { ...defaultSide },
    right: { ...defaultSide },
  });

  // Parse URL params on mount
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('design_id');
    const name = params.get('product');
    const pId = params.get('product_id');
    const sUrl = params.get('shop_url');
    // widthCm/heightCm are already set in useState initializer — no need to set again
    if (id) setDesignId(id);
    if (name) setProductName(decodeURIComponent(name));
    if (pId) setProductId(parseInt(pId, 10));
    if (sUrl) setShopUrl(decodeURIComponent(sUrl));

    // URL-based pricing params (for WooCommerce, Shopware, etc. without API)
    const ppm2 = parseFloat(params.get('price_per_m2'));
    const minP = parseFloat(params.get('min_price'));
    const shpCost = parseFloat(params.get('shipping_cost'));

    // Suppliers with price tiers (from Selenny Admin)
    let suppliersParam = null;
    try {
      const suppliersRaw = params.get('suppliers');
      if (suppliersRaw) suppliersParam = JSON.parse(decodeURIComponent(suppliersRaw));
    } catch (e) {}

    // Parse shipping tiers from URL if provided
    let shippingTiersParam = null;
    try {
      const stRaw = params.get('shipping_tiers');
      if (stRaw) shippingTiersParam = JSON.parse(decodeURIComponent(stRaw));
    } catch (e) {}

    if (suppliersParam && suppliersParam.length > 0) {
      // Full supplier-based pricing with tiers + markup
      setUrlPricing({
        suppliers: suppliersParam,
        markupPercent: parseFloat(params.get('markup_percent')) || 0,
        markupFixed: parseFloat(params.get('markup_fixed')) || 0,
        datacheckStandard: parseFloat(params.get('datacheck_standard')) || 5.0,
        datacheckExpress: parseFloat(params.get('datacheck_express')) || 15.0,
        packagingCost: parseFloat(params.get('packaging_cost')) || 0,
        shippingTiers: shippingTiersParam || [],
      });
    } else if (ppm2 > 0) {
      setUrlPricing({
        pricePerM2: ppm2,
        minPrice: minP > 0 ? minP : 5.0,
        shippingCost: shpCost > 0 ? shpCost : 0,
        setupCost: parseFloat(params.get('setup_cost')) || 0,
        datacheckStandard: parseFloat(params.get('datacheck_standard')) || 5.0,
        datacheckExpress: parseFloat(params.get('datacheck_express')) || 15.0,
        oversizeThreshold: parseFloat(params.get('oversize_threshold')) || 100,
        oversizeSurcharge: parseFloat(params.get('oversize_surcharge')) || 0,
        packagingCost: parseFloat(params.get('packaging_cost')) || 0,
      });
    }

    // Load product config from central Designer API
    const orderNum = params.get('order_number') || params.get('ordernumber');
    if (orderNum || name) {
      const productName = name ? decodeURIComponent(name) : '';
      const shopParam = params.get('shop') || '';
      let shopKey = shopParam;
      if (!shopKey) {
        const host = window.location.hostname;
        if (host.includes('xlprintservice')) shopKey = 'xlprintservice';
        else if (host.includes('geburtstagsplakat')) shopKey = 'geburtstagsplakat';
        else if (host.includes('superprint24')) shopKey = 'superprint24';
        else if (host.includes('der-banner-shop')) shopKey = 'der-banner-shop';
        else if (host.includes('ul-flieger')) shopKey = 'ul-flieger';
        else if (host.includes('ul-marktplatz')) shopKey = 'ul-marktplatz';
        else if (host.includes('selenny.de')) shopKey = 'selenny';
      }
      const shopQuery = shopKey ? `?shop=${shopKey}` : '';
      const applyProductMatch = (data) => {
          if (!data.products || data.products.length === 0) return false;
          const orderNum = params.get('order_number') || params.get('ordernumber');
          let match = null;
          if (orderNum) match = data.products.find(p => p.orderNumber === orderNum);
          // SKU match
          const skuParam = params.get('sku');
          if (!match && skuParam) match = data.products.find(p => p.sku === skuParam || p.orderNumber === skuParam);
          // Exact name match
          if (!match && productName) match = data.products.find(p => p.productName === productName);
          // Substring match
          if (!match && productName) match = data.products.find(p => productName.includes(p.productName) || p.productName.includes(productName));
          // Word-based fuzzy match: first significant word must match + at least one more word overlap
          if (!match && productName) {
            const normalize = s => s.toLowerCase().replace(/[|&\/\\,;:]+/g, ' ').split(/\s+/).filter(w => w.length > 2);
            const searchWords = normalize(productName);
            match = data.products.find(p => {
              const pWords = normalize(p.productName);
              if (!pWords.length || !searchWords.length) return false;
              if (pWords[0] !== searchWords[0]) return false;
              const overlapping = pWords.filter(w => searchWords.some(sw => sw.startsWith(w) || w.startsWith(sw)));
              return overlapping.length >= Math.max(1, Math.ceil(pWords.length * 0.5));
            });
          }
          // Only use single-product fallback if we have an order number (prevents wrong matches)
          if (!match && orderNum && data.products.length === 1) match = data.products[0];
          if (match) {
            // Pro Produkt erlaubte Clipart-Kategorien (leer/fehlt = alle anzeigen)
            setAllowedClipartCategories(Array.isArray(match.clipartCategories) ? match.clipartCategories : []);
            // Central API always wins for pricing (if suppliers available)
            if (match.suppliers && match.suppliers.length > 0) {
              setUrlPricing({
                suppliers: match.suppliers,
                markupPercent: match.markupPercent || 0,
                markupFixed: match.markupFixed || 0,
                datacheckStandard: match.datacheckStandard ?? 5.0,
                datacheckExpress: match.datacheckExpress ?? 15.0,
                packagingCost: 0,
                minWidth: match.minWidth || 0,
                maxWidth: match.maxWidth || 0,
                minHeight: match.minHeight || 0,
                maxHeight: match.maxHeight || 0,
                shippingTiers: match.shippingTiers || [],
                standardSizes: match.standardSizes || [],
                deliveryDays: match.deliveryDays || '',
                expressDeliveryDays: match.expressDeliveryDays || '',
                allowedFinishing: match.allowedFinishing || [],
                allowedHemWidths: match.allowedHemWidths || [],
                allowedGrommetSizes: match.allowedGrommetSizes || [],
                allowedGrommetSpacings: match.allowedGrommetSpacings || [],
                allowPerSideFinishing: !!match.allowPerSideFinishing,
              });
            }
            if (!name && match.productName) setProductName(match.productName);
            // Central API values always win for dimensions (user manages them in Designzentrum)
            if (match.width > 0) setWidthCm(match.width);
            if (match.height > 0) setHeightCm(match.height);
            // Beschnitt pro Produkt
            if (match.bleedCm != null) setBleedCm(match.bleedCm);
            // Druck-DPI pro Produkt (Designer-Backend)
            if (match.printDpi) {
              const dpi = parseInt(match.printDpi, 10);
              if (dpi >= 72 && dpi <= 1200) setPrintDpi(dpi);
            }
            if (match.finishingConfig) {
              try {
                const fc = JSON.parse(match.finishingConfig);
                if (fc && fc.mode) setFinishing(fc);
              } catch (e) {}
            }
            return true;
          }
          return false;
      };
      const fetchUrl = PRODUCTS_API + '/products/public' + shopQuery;
      fetch(fetchUrl)
        .then(r => r.ok ? r.json() : Promise.reject('HTTP ' + r.status))
        .then(data => {
          if (!applyProductMatch(data)) {
            return fetch(API_BASE + '/products/public').then(r => r.json()).then(data2 => {
              applyProductMatch(data2);
              setAwaitingApiDims(false);
            });
          }
          setAwaitingApiDims(false);
        })
        .catch(() => {
          fetch(API_BASE + '/products/public').then(r => r.json()).then(data => {
            applyProductMatch(data);
            setAwaitingApiDims(false);
          }).catch(() => { setAwaitingApiDims(false); });
        });
    }
    const wa = params.get('whatsapp');
    if (wa) setWhatsappNumber(wa);
    const waText = params.get('whatsapp_text');
    if (waText) setWhatsappText(decodeURIComponent(waText));

    // Default finishing from URL params
    const finishingConfigParam = params.get('finishing_config');
    if (finishingConfigParam) {
      try {
        const fc = JSON.parse(decodeURIComponent(finishingConfigParam));
        if (fc && fc.mode) {
          setFinishing(fc);
        }
      } catch (e) {
        console.warn('Could not parse finishing_config:', e);
      }
    } else {
      // Legacy: simple finishing_type param (e.g. from WooCommerce)
      const fType = params.get('finishing_type');
      if (fType && fType !== 'cut') {
        const saumCm = parseInt(params.get('saum_cm')) || 3;
        const gSpacing = parseInt(params.get('grommet_spacing')) || 50;
        const gSize = parseInt(params.get('grommet_size')) || 11;
        const newSide = {
          type: fType,
          saumCm,
          grommetMode: 'every' + gSpacing,
          grommetSize: gSize,
          hohlsaumCm: 5,
          kederType: 'round-white',
          klettPos: 'front',
        };
        setFinishing({
          mode: 'all',
          all: newSide,
          top: { ...newSide },
          bottom: { ...newSide },
          left: { ...newSide },
          right: { ...newSide },
        });
      }
    }
  }, []);

  // Apply dark mode class
  useEffect(() => {
    document.documentElement.classList.toggle('dark', darkMode);
    localStorage.setItem('selenny-dark', darkMode);
  }, [darkMode]);

  // Load existing design if design_id is in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const id = params.get('design_id');
    if (!id) return;

    const loadDesign = async () => {
      try {
        setDesignLoadStatus('Design wird geladen...');
        setDesignLoadProgress(10);
        const res = await fetch(`${API_BASE}/designs/${id}`);
        if (!res.ok) { setDesignLoading(false); return; }
        const data = await res.json();
        setDesignLoadProgress(30);
        setDesignLoadStatus('Canvas wird vorbereitet...');

        // Wait for canvas to be ready
        const waitForCanvas = setInterval(() => {
          const cm = canvasManagerRef.current;
          const vm = viewManagerRef.current;
          if (!cm?.canvas) return;
          clearInterval(waitForCanvas);

          setDesignLoadProgress(40);
          setDesignLoadStatus('Abmessungen werden gesetzt...');

          // 1. Set dimensions from saved design FIRST (updates canvas + React state)
          const savedW = parseFloat(data.width_cm) || widthCm;
          const savedH = parseFloat(data.height_cm) || heightCm;
          setWidthCm(savedW);
          setHeightCm(savedH);
          cm.setDimensions(savedW, savedH);
          if (data.name) setProductName(data.name);

          // Restore finishing settings (state only — visual applied after canvas load)
          if (data.finishing) setFinishing(data.finishing);
          if (data.bleed_cm != null) setBleedCm(parseFloat(data.bleed_cm));
          if (data.hem_cm != null) setHemCm(parseFloat(data.hem_cm));
          if (data.grommets) setGrommets(data.grommets);
          if (data.hohlsaum) setHohlsaum(data.hohlsaum);

          // TEMPLATE PROTECTION: If the loaded design is a template,
          // clear designId so the next save creates a NEW design (copy) instead of overwriting
          if (data.is_template == 1 || data.is_template === true) {
            setDesignId(null);
            console.info('Template loaded via design_id — designId cleared to prevent overwrite');
          }

          // Check for expiration warning
          if (data.expires_in_days != null && data.expires_in_days <= 30) {
            setExpiresInDays(data.expires_in_days);
            setShowExpiryWarning(true);
          }

          setDesignLoadProgress(50);
          setDesignLoadStatus('Elemente werden geladen...');

          // 2. Load canvas content (views or single canvas JSON)
          // Robust: loadJSON wirft jetzt bei Parse-Fehler → wir fangen ihn ab,
          // damit der Designer NICHT mit leerer Bühne hängen bleibt.
          const safeLoad = (() => {
            if (data.views && Array.isArray(data.views) && vm) {
              return Promise.resolve(vm.loadAllViews(data.views)).catch((err) => {
                console.error('[Designer] loadAllViews failed:', err);
                toast.error('Design konnte nicht vollständig geladen werden. Bühne bleibt leer.');
              });
            }
            if (data.canvas_json) {
              return Promise.resolve(cm.loadJSON(data.canvas_json)).catch((err) => {
                console.error('[Designer] canvas_json laden fehlgeschlagen:', err);
                toast.error('Design-Daten beschädigt. Bühne wurde geleert. Bitte aus Backup oder Vorlage neu starten.');
              });
            }
            return Promise.resolve();
          })();

          // 3. After content is loaded, wait for fonts, re-apply dimensions, finishing visuals, and fit zoom
          Promise.resolve(safeLoad).then(async () => {
            setDesignLoadProgress(70);
            setDesignLoadStatus('Schriften werden geladen...');
            // Wait for all fonts used in the design to be fully loaded
            const usedFonts = new Set();
            cm.canvas.getObjects().forEach(obj => {
              if (obj.fontFamily) usedFonts.add(obj.fontFamily);
              if (obj.styles) {
                Object.values(obj.styles).forEach(line => {
                  if (line) Object.values(line).forEach(charStyle => {
                    if (charStyle && charStyle.fontFamily) usedFonts.add(charStyle.fontFamily);
                  });
                });
              }
            });
            if (usedFonts.size > 0) {
              try {
                const fontPromises = [...usedFonts].map(f => document.fonts.load(`bold 40px "${f}"`).catch(() => null));
                await Promise.all(fontPromises);
                // Re-initialize text dimensions after fonts are loaded
                cm.canvas.getObjects().forEach(obj => {
                  if (obj.type === 'i-text' || obj.type === 'textbox' || obj.type === 'text') {
                    obj.initDimensions();
                    obj.setCoords();
                  }
                });
              } catch (e) {
                console.warn('Font preload warning:', e);
              }
            }

            setDesignLoadProgress(85);
            setDesignLoadStatus('Verarbeitung wird angewendet...');

            cm.setDimensions(savedW, savedH);

            // Apply finishing visuals to canvas
            if (data.finishing && data.finishing.mode) {
              const defaultSide = { type: 'cut', saumCm: 3, grommetMode: 'every50', grommetSize: 11, hohlsaumCm: 5 };
              const f = data.finishing;
              const _isPerSide = f.mode === 'perSide' || f.mode === 'individual';
              const sides = _isPerSide
                ? { top: f.top || defaultSide, bottom: f.bottom || defaultSide, left: f.left || defaultSide, right: f.right || defaultSide }
                : { top: f.all || defaultSide, bottom: f.all || defaultSide, left: f.all || defaultSide, right: f.all || defaultSide };

              const anyActive = Object.values(sides).some(s => s && s.type && s.type !== 'cut');
              if (anyActive) {
                const hp = {
                  top: (sides.top.type === 'hem' || sides.top.type === 'hem-grommets') ? (sides.top.saumCm || 3) : 0,
                  bottom: (sides.bottom.type === 'hem' || sides.bottom.type === 'hem-grommets') ? (sides.bottom.saumCm || 3) : 0,
                  left: (sides.left.type === 'hem' || sides.left.type === 'hem-grommets') ? (sides.left.saumCm || 3) : 0,
                  right: (sides.right.type === 'hem' || sides.right.type === 'hem-grommets') ? (sides.right.saumCm || 3) : 0,
                };
                setHemCm(Math.max(hp.top, hp.bottom, hp.left, hp.right));
                setHemPerSide(hp);
                cm.drawHemLines(hp);

                const grommetSides = {
                  top: sides.top.type === 'grommets' || sides.top.type === 'hem-grommets',
                  bottom: sides.bottom.type === 'grommets' || sides.bottom.type === 'hem-grommets',
                  left: sides.left.type === 'grommets' || sides.left.type === 'hem-grommets',
                  right: sides.right.type === 'grommets' || sides.right.type === 'hem-grommets',
                };
                if (Object.values(grommetSides).some(Boolean)) {
                  const firstGrommetSide = sides.top.type.includes('grommets') ? sides.top
                    : sides.bottom.type.includes('grommets') ? sides.bottom
                    : sides.left.type.includes('grommets') ? sides.left
                    : sides.right;
                  const gMode = firstGrommetSide.grommetMode || 'every50';
                  const gc = {
                    enabled: true,
                    mode: gMode === 'corners' ? 'corners' : 'custom',
                    customSpacingCm: parseInt(String(gMode).replace('every', '')) || 50,
                    radiusMm: firstGrommetSide.grommetSize || 11,
                    sides: grommetSides,
                    hemPerSide: hp,
                  };
                  setGrommets(gc);
                  cm.drawGrommets(gc, hp);
                }

                const hohlsaumPerSide = {
                  top: sides.top.type === 'hohlsaum' ? (sides.top.hohlsaumCm || 5) : 0,
                  bottom: sides.bottom.type === 'hohlsaum' ? (sides.bottom.hohlsaumCm || 5) : 0,
                  left: sides.left.type === 'hohlsaum' ? (sides.left.hohlsaumCm || 5) : 0,
                  right: sides.right.type === 'hohlsaum' ? (sides.right.hohlsaumCm || 5) : 0,
                };
                if (Object.values(hohlsaumPerSide).some(v => v > 0)) {
                  setHohlsaum(hohlsaumPerSide);
                  if (typeof cm.drawHohlsaum === 'function') cm.drawHohlsaum(hohlsaumPerSide);
                }
              }
            }
            if (data.hohlsaum) {
              cm.drawHohlsaum(data.hohlsaum);
            }
            if (data.bleed_cm != null) {
              cm.bleedCm = parseFloat(data.bleed_cm);
              cm.drawBleedLines(parseFloat(data.bleed_cm));
            }

            cm.canvas.renderAll();
            // Trigger fit-zoom so canvas is centered on screen
            setZoom(0);

            setDesignLoadProgress(100);
            setDesignLoadStatus('Fertig!');
            setTimeout(() => setDesignLoading(false), 400);
          });

          toast.success('Design geladen');
        }, 200);

        // Safety timeout
        setTimeout(() => { clearInterval(waitForCanvas); setDesignLoading(false); }, 15000);
      } catch (err) {
        console.error('Design laden fehlgeschlagen:', err);
        setDesignLoading(false);
      }
    };
    loadDesign();
  }, []);

  // Load shared design if ?shared= token is in URL
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sharedToken = params.get('shared');
    if (!sharedToken) return;

    const loadShared = async () => {
      try {
        setDesignLoadStatus('Geteiltes Design wird geladen...');
        setDesignLoading(true);
        setDesignLoadProgress(10);
        const res = await fetch(`${API_BASE}/shared/${sharedToken}`);
        if (!res.ok) {
          toast.error('Geteiltes Design nicht gefunden');
          setDesignLoading(false);
          return;
        }
        const data = await res.json();
        setDesignLoadProgress(30);

        setIsSharedView(true);
        setSharedDesignName(data.name || 'Unbenanntes Design');

        // Wait for canvas
        const waitForCanvas = setInterval(() => {
          const cm = canvasManagerRef.current;
          const vm = viewManagerRef.current;
          if (!cm?.canvas) return;
          clearInterval(waitForCanvas);

          setDesignLoadProgress(50);

          const savedW = parseFloat(data.width_cm) || widthCm;
          const savedH = parseFloat(data.height_cm) || heightCm;
          setWidthCm(savedW);
          setHeightCm(savedH);
          cm.setDimensions(savedW, savedH);
          if (data.name) setProductName(data.name);

          if (data.finishing) setFinishing(data.finishing);
          if (data.bleed_cm != null) setBleedCm(parseFloat(data.bleed_cm));
          if (data.hem_cm != null) setHemCm(parseFloat(data.hem_cm));
          if (data.grommets) setGrommets(data.grommets);
          if (data.hohlsaum) setHohlsaum(data.hohlsaum);

          setDesignLoadProgress(60);

          const loadPromise = (data.views && Array.isArray(data.views) && vm)
            ? Promise.resolve(vm.loadAllViews(data.views)).catch((err) => {
                console.error('[Designer/Shared] loadAllViews failed:', err);
                toast.error('Geteiltes Design konnte nicht vollständig geladen werden.');
              })
            : data.canvas_json
              ? Promise.resolve(cm.loadJSON(data.canvas_json)).catch((err) => {
                  console.error('[Designer/Shared] canvas_json laden fehlgeschlagen:', err);
                  toast.error('Geteilte Design-Daten beschädigt.');
                })
              : Promise.resolve();

          Promise.resolve(loadPromise).then(() => {
            setDesignLoadProgress(90);
            // Make canvas read-only
            cm.canvas.selection = false;
            cm.canvas.getObjects().forEach(obj => {
              obj.selectable = false;
              obj.evented = false;
            });
            cm.canvas.renderAll();

            setDesignLoadProgress(100);
            setDesignLoading(false);
            toast.success('Geteiltes Design geladen (Nur Vorschau)');
          });
        }, 100);
      } catch (err) {
        console.error('Shared design load failed:', err);
        toast.error('Fehler beim Laden des geteilten Designs');
        setDesignLoading(false);
      }
    };
    loadShared();
  }, []);

  // Load template design if template_id is in URL (creates a NEW design based on the template)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const templateId = params.get('template_id');
    const designIdParam = params.get('design_id');
    // Only load template if template_id is present and design_id is NOT
    if (!templateId || designIdParam) return;

    const loadTemplate = async () => {
      try {
        setDesignLoadStatus('Vorlage wird geladen...');
        setDesignLoadProgress(10);
        const res = await fetch(`${API_BASE}/designs/${templateId}`);
        if (!res.ok) {
          console.error('Vorlage nicht gefunden:', templateId);
          setDesignLoading(false);
          return;
        }
        const data = await res.json();
        setDesignLoadProgress(30);
        setDesignLoadStatus('Canvas wird vorbereitet...');

        // Wait for canvas to be ready
        const waitForCanvas = setInterval(() => {
          const cm = canvasManagerRef.current;
          const vm = viewManagerRef.current;
          if (!cm?.canvas) return;
          clearInterval(waitForCanvas);

          setDesignLoadProgress(40);

          // 1. Set dimensions from template
          const savedW = parseFloat(data.width_cm) || widthCm;
          const savedH = parseFloat(data.height_cm) || heightCm;
          setWidthCm(savedW);
          setHeightCm(savedH);
          cm.setDimensions(savedW, savedH);
          if (data.name) setProductName(data.name);

          setDesignLoadProgress(50);
          setDesignLoadStatus('Elemente werden geladen...');

          // 2. Load canvas content (views or single canvas JSON)
          const loadPromise = (data.views && Array.isArray(data.views) && vm)
            ? Promise.resolve(vm.loadAllViews(data.views)).catch((err) => {
                console.error('[Designer/Template] loadAllViews failed:', err);
                toast.error('Vorlage konnte nicht vollständig geladen werden.');
              })
            : data.canvas_json
              ? Promise.resolve(cm.loadJSON(data.canvas_json)).catch((err) => {
                  console.error('[Designer/Template] canvas_json laden fehlgeschlagen:', err);
                  toast.error('Vorlagen-Daten beschädigt.');
                })
              : Promise.resolve();

          // 3. After content is loaded, re-apply dimensions and fit zoom
          // NOTE: designId is intentionally NOT set — saving will create a new design
          Promise.resolve(loadPromise).then(() => {
            setDesignLoadProgress(90);
            setDesignLoadStatus('Fast fertig...');
            cm.setDimensions(savedW, savedH);
            setZoom(0);
            setDesignLoadProgress(100);
            setDesignLoadStatus('Fertig!');
            setTimeout(() => setDesignLoading(false), 400);
          });

          toast.success('Vorlage geladen');
        }, 200);

        // Safety timeout
        setTimeout(() => { clearInterval(waitForCanvas); setDesignLoading(false); }, 15000);
      } catch (err) {
        console.error('Vorlage laden fehlgeschlagen:', err);
        setDesignLoading(false);
      }
    };
    loadTemplate();
  }, []);

  // Auto-export mode: triggered from order confirmation page via hidden iframe
  // URL: /designer/?design_id=XXX&width=W&height=H&bleed=1&autoexport=1
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('autoexport') !== '1') return;
    const dId = params.get('design_id');
    if (!dId) return;

    let loadComplete = false;
    let exportStarted = false;

    // Listen for design load completion (from the design_id useEffect)
    const origToast = window.__autoexportReady;
    // The design_id useEffect calls toast.success('Design geladen') when done.
    // We hook into canvas events to detect load completion more reliably.
    const checkAndExport = async () => {
      if (exportStarted) return;
      exportStarted = true;

      const cm = canvasManagerRef.current;
      if (!cm?.canvas) {
        window.parent.postMessage({ type: 'autoexport-done', designId: dId, success: false, error: 'No canvas' }, '*');
        return;
      }

      try {
        const bleed = parseFloat(params.get('bleed') || '1');
        const w = parseFloat(params.get('width')) || cm.widthCm;
        const h = parseFloat(params.get('height')) || cm.heightCm;

        // Apply bleed to CanvasManager so export methods use correct value
        cm.bleedCm = bleed;

        // 1. SVG export (vector — text stays sharp)
        let svgData = null;
        try { svgData = cm.toExportSVG(); } catch (e) { console.warn('SVG export failed:', e); }

        // 2. PNG export at 300 DPI with bleed
        let pngData = null;
        const _exportDpi = (() => {
          if (printDpi && printDpi >= 72 && printDpi <= 1200) return printDpi;
          const p = new URLSearchParams(window.location.search);
          const d = parseInt(p.get('designer_dpi') || p.get('dpi') || '144', 10);
          return (d >= 72 && d <= 1200) ? d : 144;
        })();
        try { pngData = await cm.toExportWithBleed({ dpi: _exportDpi }); } catch (e) { console.warn('PNG export failed:', e); }

        // 3. Send to backend
        if (svgData || (pngData && pngData.length > 100)) {
          const exportPayload = {
              svg_data: svgData || null,
              png_data: pngData || null,
              dpi: _exportDpi,
              width_cm: w,
              height_cm: h,
              bleed_cm: bleed,
              total_width_cm: w + 2 * bleed,
              total_height_cm: h + 2 * bleed,
              internal_scale: cm.internalScale || 1,
          };
          if (cm._printFileOriginal) {
            exportPayload.print_file = cm._printFileOriginal;
            exportPayload.print_file_name = cm._printFileName || 'druckdatei';
          }
          // Include admin auth for autoexport (passed via URL params from admin panel)
          const authUser = params.get('_user') || sessionStorage.getItem('selenny-admin-user');
          const authPass = params.get('_pass') || sessionStorage.getItem('selenny-admin-pass');
          if (authUser) exportPayload._user = authUser;
          if (authPass) exportPayload._pass = authPass;
          const resp = await fetch(`${API_BASE}/export/${dId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(exportPayload),
          });
          const result = await resp.json();
          window.parent.postMessage({ type: 'autoexport-done', designId: dId, success: resp.ok, result }, '*');
        } else {
          window.parent.postMessage({ type: 'autoexport-done', designId: dId, success: false, error: 'No export data generated' }, '*');
        }
      } catch (err) {
        window.parent.postMessage({ type: 'autoexport-done', designId: dId, success: false, error: err.message }, '*');
      }
    };

    // Poll: wait for canvas objects AND stable state (no new objects for 1s)
    let lastObjectCount = 0;
    let stableCount = 0;
    const interval = setInterval(() => {
      const cm = canvasManagerRef.current;
      if (!cm?.canvas) return;
      const count = cm.canvas.getObjects().length;
      if (count > 0 && count === lastObjectCount) {
        stableCount++;
        // Objects stable for 1.5s (3 checks at 500ms) — design is loaded
        if (stableCount >= 3) {
          clearInterval(interval);
          checkAndExport();
        }
      } else {
        lastObjectCount = count;
        stableCount = 0;
      }
    }, 500);

    // Safety timeout: export whatever we have after 15s
    setTimeout(() => {
      clearInterval(interval);
      if (!exportStarted) checkAndExport();
    }, 15000);
  }, []);

  // PostMessage bridge for embedded mode (canvas preview export)
  useEffect(() => {
    if (!isEmbedded) return;
    const handler = (e) => {
      if (e.data?.type === 'toggle-preview') {
        setPreviewMode(prev => !prev);
        return;
      }
      if (e.data?.type === 'get-designer-preview') {
        const cm = canvasManagerRef.current;
        let preview = null;
        if (cm?.canvas) {
          try {
            preview = cm.toDataURL({ format: 'jpeg', quality: 0.5, multiplier: 0.25 });
          } catch (err) {
            console.warn('Preview export failed:', err);
          }
        }
        window.parent.postMessage({ type: 'designer-preview-response', preview }, '*');
      }
      // Overlay triggers add-to-cart — delegates to designer's full save+export flow
      if (e.data?.type === 'trigger-add-to-cart') {
        handleAddToCartRef.current?.(pricingRef.current);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [isEmbedded]);

  // Initialize canvas after first render (wait for API dimensions if needed)
  useEffect(() => {
    if (awaitingApiDims) return; // Don't init canvas until API dimensions arrive
    if (isInitialized) return; // Already initialized

    // Small delay to ensure canvas DOM element exists
    const timer = setTimeout(() => {
      const cm = new CanvasManager('design-canvas', widthCm, heightCm);
      cm.initCanvas();
      canvasManagerRef.current = cm;

      const hm = new HistoryManager(cm);
      historyManagerRef.current = hm;
      hm.init();

      const gm = new GridManager(cm);
      gridManagerRef.current = gm;

      const lm = new LayerManager(cm);
      layerManagerRef.current = lm;

      const vm = new ViewManager(cm);
      viewManagerRef.current = vm;
      vm.init();

      const imgf = new ImageFilters(cm);
      imageFiltersRef.current = imgf;

      bgRemovalRef.current = new BackgroundRemoval(cm);

      setIsInitialized(true);
      forceUpdate((n) => n + 1); // Force re-render so toolbars get the refs
    }, 100);

    return () => {
      clearTimeout(timer);
      if (!isInitialized) return; // Don't dispose if never initialized
      if (gridManagerRef.current) gridManagerRef.current.dispose();
      if (canvasManagerRef.current) canvasManagerRef.current.dispose();
      eventBus.removeAllListeners();
    };
  }, [awaitingApiDims]); // eslint-disable-line

  // Event listeners
  useEffect(() => {
    if (!isInitialized) return;

    const unsubs = [
      eventBus.on('stage:deselect', () => {
        // Clicking on the gray area around the canvas - deselect objects
        if (canvasManagerRef.current?.canvas) {
          canvasManagerRef.current.canvas.discardActiveObject();
          canvasManagerRef.current.canvas.renderAll();
        }
        setActiveObject(null);
      }),
      eventBus.on('object:selected', () => {
        const obj = canvasManagerRef.current?.getActiveObject() || null;
        setActiveObject(obj);
        refreshLayers();
        // Reopen CollageImageUploader when clicking on a collage object
        if (obj && (obj.isCollagePlaceholder || obj.isCollageImage || obj.isCollageLabel)) {
          const cId = obj.collageId;
          if (cId) {
            const allObjs = canvasManagerRef.current.canvas.getObjects();
            const placeholders = allObjs.filter(o => o.isCollagePlaceholder && o.collageId === cId);
            if (placeholders.length > 0) {
              setActiveCollage({ collageId: cId, placeholderCount: placeholders.length });
              setCollageMinimized(false);
            }
          }
        }
      }),
      eventBus.on('selection:cleared', () => {
        setActiveObject(null);
      }),
      eventBus.on('canvas:changed', () => {
        refreshLayers();
      }),
      eventBus.on('history:changed', (data) => {
        setCanUndo(data.canUndo);
        setCanRedo(data.canRedo);
      }),
      eventBus.on('views:changed', (data) => {
        setViews(data.views);
        setActiveViewIndex(data.activeIndex);
      }),
      eventBus.on('grid:changed', (data) => {
        setGridVisible(data.visible);
      }),
      eventBus.on('text:edit-request', (data) => {
        if (data.target) {
          setEditingTextObj(data.target);
          if (data.isCurved) {
            setEditingTextIsCurved(true);
            setEditingTextValue(data.text || '');
            setEditingTextColor(data.fill || '#000000');
            setEditingTextFont(data.fontFamily || 'Inter');
            setEditingTextSize(data.fontSize || 24);
          } else {
            setEditingTextIsCurved(false);
            setEditingTextValue(data.target.text || '');
            setEditingTextColor(data.target.fill || '#000000');
            setEditingTextFont(data.target.fontFamily || 'Inter');
            setEditingTextSize(data.target.fontSize || 24);
          }
        }
      }),
      eventBus.on('overlay:colors', (data) => {
        setOverlayColors(data);
      }),
      eventBus.on('object:added', (data) => {
        const obj = data?.target;
        if (obj && obj.type && !obj.isBleed && !obj.isHem && !obj.isHohlsaum && !obj.isGrommet && !obj.isGrid && !obj.isSnapLine && !obj.isGuide && !obj.isCollagePlaceholder && !obj.isCollageLabel && !obj.isCollageImage && !obj.isNumberOutline) {
          speech.objectAdded(obj.type);
        }
      }),
      eventBus.on('collage:created', (data) => {
        if (data?.collageId && data?.placeholderCount) {
          setActiveCollage({ collageId: data.collageId, placeholderCount: data.placeholderCount });
          setCollageMinimized(false);
        }
      }),
      eventBus.on('collage:placeholder-clicked', (data) => {
        if (data?.collageId && data?.placeholderCount) {
          setActiveCollage({ collageId: data.collageId, placeholderCount: data.placeholderCount });
          setCollageMinimized(false);
        }
      }),
      eventBus.on('bgimage:changed', (has) => setHasBgImage(has)),
    ];

    refreshLayers();

    return () => unsubs.forEach((unsub) => unsub());
  }, [isInitialized]); // eslint-disable-line

  // Keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (!isInitialized) return;
      const tag = e.target.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      const cm = canvasManagerRef.current;
      const hm = historyManagerRef.current;

      if ((e.metaKey || e.ctrlKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        hm.undo();
      } else if ((e.metaKey || e.ctrlKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        hm.redo();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'd') {
        e.preventDefault();
        cm.duplicateSelected();
      } else if (e.key === 'Delete' || (e.key === 'Backspace' && !e.metaKey)) {
        cm.deleteSelected();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'g') {
        e.preventDefault();
        e.shiftKey ? cm.ungroupSelected() : cm.groupSelected();
      } else if ((e.metaKey || e.ctrlKey) && e.key === 'l') {
        e.preventDefault();
        cm.toggleLock();
      } else if (e.key === 'Escape') {
        cm.deselectAll();
      } else if (e.key === 'ArrowUp' || e.key === 'ArrowDown' || e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        const active = cm.canvas.getActiveObject();
        if (!active) return;
        e.preventDefault();
        const step = e.shiftKey ? 10 : 1;
        const dx = e.key === 'ArrowLeft' ? -step : e.key === 'ArrowRight' ? step : 0;
        const dy = e.key === 'ArrowUp' ? -step : e.key === 'ArrowDown' ? step : 0;
        active.set({ left: (active.left || 0) + dx, top: (active.top || 0) + dy });
        active.setCoords();
        cm.canvas.requestRenderAll();
        cm.canvas.fire('object:modified', { target: active });
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isInitialized]);

  // Apply default finishing from URL params / API after canvas is ready
  useEffect(() => {
    if (!isInitialized) return;
    const mgr = canvasManagerRef.current;
    if (!mgr) return;
    const defaultSide = { type: 'cut', saumCm: 3, grommetMode: 'every50', grommetSize: 11, hohlsaumCm: 5 };
    const isPerSide = finishing?.mode === 'perSide' || finishing?.mode === 'individual';
    const sides = isPerSide
      ? {
          top: finishing.top || defaultSide,
          bottom: finishing.bottom || defaultSide,
          left: finishing.left || defaultSide,
          right: finishing.right || defaultSide,
        }
      : { top: finishing.all, bottom: finishing.all, left: finishing.all, right: finishing.all };

    // Apply only when at least one side has a non-'cut' type
    const anyActive = Object.values(sides).some(s => s && s.type && s.type !== 'cut');
    if (!anyActive) return;

    const hp = {
      top: (sides.top.type === 'hem' || sides.top.type === 'hem-grommets') ? (sides.top.saumCm || 3) : 0,
      bottom: (sides.bottom.type === 'hem' || sides.bottom.type === 'hem-grommets') ? (sides.bottom.saumCm || 3) : 0,
      left: (sides.left.type === 'hem' || sides.left.type === 'hem-grommets') ? (sides.left.saumCm || 3) : 0,
      right: (sides.right.type === 'hem' || sides.right.type === 'hem-grommets') ? (sides.right.saumCm || 3) : 0,
    };
    const maxHem = Math.max(hp.top, hp.bottom, hp.left, hp.right);
    setHemCm(maxHem);
    setHemPerSide(hp);
    mgr.drawHemLines(hp);

    const grommetSides = {
      top: sides.top.type === 'grommets' || sides.top.type === 'hem-grommets',
      bottom: sides.bottom.type === 'grommets' || sides.bottom.type === 'hem-grommets',
      left: sides.left.type === 'grommets' || sides.left.type === 'hem-grommets',
      right: sides.right.type === 'grommets' || sides.right.type === 'hem-grommets',
    };
    const hasGrommets = Object.values(grommetSides).some(Boolean);
    if (hasGrommets) {
      const firstGrommetSide = sides.top.type.includes('grommets') ? sides.top
        : sides.bottom.type.includes('grommets') ? sides.bottom
        : sides.left.type.includes('grommets') ? sides.left
        : sides.right;
      const gMode = firstGrommetSide.grommetMode || 'every50';
      const gc = {
        enabled: true,
        mode: gMode === 'corners' ? 'corners' : 'custom',
        customSpacingCm: parseInt(String(gMode).replace('every', '')) || 50,
        radiusMm: firstGrommetSide.grommetSize || 11,
        sides: grommetSides,
        hemPerSide: hp,
      };
      setGrommets(gc);
      mgr.drawGrommets(gc, hp);
    }

    // Hohlsaum per side
    const hohlsaumPerSide = {
      top: sides.top.type === 'hohlsaum' ? (sides.top.hohlsaumCm || 5) : 0,
      bottom: sides.bottom.type === 'hohlsaum' ? (sides.bottom.hohlsaumCm || 5) : 0,
      left: sides.left.type === 'hohlsaum' ? (sides.left.hohlsaumCm || 5) : 0,
      right: sides.right.type === 'hohlsaum' ? (sides.right.hohlsaumCm || 5) : 0,
    };
    if (Object.values(hohlsaumPerSide).some(v => v > 0)) {
      setHohlsaum(hohlsaumPerSide);
      if (typeof mgr.drawHohlsaum === 'function') mgr.drawHohlsaum(hohlsaumPerSide);
    }

    mgr.canvas.renderAll();
  }, [isInitialized, finishing]); // eslint-disable-line

  const refreshLayers = useCallback(() => {
    if (layerManagerRef.current) {
      setLayers(layerManagerRef.current.getLayers());
    }
  }, []);

  const handleDimensionChange = useCallback((w, h) => {
    setWidthCm(w);
    setHeightCm(h);
    setZoom(0); // trigger auto-fit after dimension change
    if (canvasManagerRef.current) {
      // fitContent=true: scale all objects proportionally to fit new dimensions
      const hasContent = canvasManagerRef.current.canvas.getObjects().some(
        obj => !obj.isHelper && obj.customType !== 'bleedLine' && obj.customType !== 'hemLine' && obj.customType !== 'grommet'
      );
      canvasManagerRef.current.setDimensions(w, h, hasContent);
    }
  }, []);

  const handleBleedChange = useCallback((cm) => {
    setBleedCm(cm);
    const mgr = canvasManagerRef.current;
    if (mgr) {
      mgr.drawBleedLines(cm);
      mgr.drawHemLines(hemCm);
      mgr.drawGrommets(grommets, hemCm);
      mgr.canvas.renderAll();
    }
  }, [hemCm, grommets]);

  const handleHemChange = useCallback((cm) => {
    setHemCm(cm);
    const mgr = canvasManagerRef.current;
    if (mgr) {
      mgr.drawHemLines(cm);
      mgr.drawGrommets(grommets, cm);
      mgr.canvas.renderAll();
    }
  }, [grommets]);

  const handleGrommetsChange = useCallback((config) => {
    setGrommets(config);
    const mgr = canvasManagerRef.current;
    if (mgr) {
      mgr.drawGrommets(config, hemCm);
      mgr.canvas.renderAll();
    }
  }, [hemCm]);

  const handleHohlsaumChange = useCallback((config) => {
    setHohlsaum(config);
    const mgr = canvasManagerRef.current;
    if (mgr) {
      mgr.drawHohlsaum(config);
      mgr.canvas.renderAll();
    }
  }, []);

  // Sync per-side finishing to canvas overlays
  const handleFinishingChange = useCallback((newFinishing) => {
    setFinishing(newFinishing);
    const mgr = canvasManagerRef.current;
    if (!mgr) return;

    const defaultSide = { type: 'cut', saumCm: 3, grommetMode: 'every50', grommetSize: 11, hohlsaumCm: 5 };
    const sides = newFinishing.mode === 'all'
      ? { top: newFinishing.all || defaultSide, bottom: newFinishing.all || defaultSide, left: newFinishing.all || defaultSide, right: newFinishing.all || defaultSide }
      : { top: newFinishing.top || defaultSide, bottom: newFinishing.bottom || defaultSide, left: newFinishing.left || defaultSide, right: newFinishing.right || defaultSide };

    // Derive per-side hem
    const hemPerSide = {
      top: (sides.top.type === 'hem' || sides.top.type === 'hem-grommets') ? (sides.top.saumCm || 3) : 0,
      bottom: (sides.bottom.type === 'hem' || sides.bottom.type === 'hem-grommets') ? (sides.bottom.saumCm || 3) : 0,
      left: (sides.left.type === 'hem' || sides.left.type === 'hem-grommets') ? (sides.left.saumCm || 3) : 0,
      right: (sides.right.type === 'hem' || sides.right.type === 'hem-grommets') ? (sides.right.saumCm || 3) : 0,
    };
    const maxHem = Math.max(hemPerSide.top, hemPerSide.bottom, hemPerSide.left, hemPerSide.right);
    setHemCm(maxHem);
    setHemPerSide(hemPerSide);
    mgr.drawHemLines(hemPerSide);

    // Derive per-side grommets
    const grommetSides = {
      top: sides.top.type === 'grommets' || sides.top.type === 'hem-grommets',
      bottom: sides.bottom.type === 'grommets' || sides.bottom.type === 'hem-grommets',
      left: sides.left.type === 'grommets' || sides.left.type === 'hem-grommets',
      right: sides.right.type === 'grommets' || sides.right.type === 'hem-grommets',
    };
    const hasGrommets = Object.values(grommetSides).some(Boolean);
    const grommetSide = Object.values(sides).find(s => s.type === 'grommets' || s.type === 'hem-grommets') || {};
    const grommetMode = grommetSide.grommetMode || 'every50';
    const grommetConfig = {
      enabled: hasGrommets,
      mode: grommetMode === 'corners' ? 'corners' : 'custom',
      customSpacingCm: parseInt(String(grommetMode).replace('every', '')) || 50,
      radiusMm: grommetSide.grommetSize || 11,
      sides: grommetSides,
      hemPerSide,
    };
    setGrommets(grommetConfig);
    mgr.drawGrommets(grommetConfig, hemPerSide);

    // Derive hohlsaum — in "all" mode, direction determines which sides get hohlsaum
    const hsVal = (side) => {
      if (side.type !== 'hohlsaum') return 0;
      return side.hohlsaumCm || 5;
    };
    let hs;
    if (newFinishing.mode === 'all' && sides.top.type === 'hohlsaum') {
      const dir = sides.top.hohlsaumDir || 'topBottom';
      const cm = sides.top.hohlsaumCm || 5;
      hs = dir === 'leftRight'
        ? { top: 0, bottom: 0, left: cm, right: cm }
        : { top: cm, bottom: cm, left: 0, right: 0 };
    } else {
      hs = {
        top: hsVal(sides.top), bottom: hsVal(sides.bottom),
        left: hsVal(sides.left), right: hsVal(sides.right),
      };
    }
    setHohlsaum(hs);
    mgr.drawHohlsaum(hs);

    mgr.canvas.renderAll();

    // Speech feedback for finishing type
    if (newFinishing.mode === 'all') {
      speech.finishingChanged(newFinishing.all.type, newFinishing.all);
    }
  }, []);

  const handleZoomChange = useCallback((z) => {
    setZoom(z);
  }, []);

  const handleFitZoom = useCallback(() => {
    // Trigger auto-fit by setting a special value that CanvasArea will pick up
    setZoom(0); // Will be recalculated by CanvasArea
  }, []);

  const handleSaveDesign = useCallback(async (options) => {
    const cm = canvasManagerRef.current;
    const vm = viewManagerRef.current;
    if (!cm) return;

    const allViews = vm.getAllViewsJSON();
    const preview = cm.toDataURL({ format: 'png', quality: 0.5, multiplier: 0.3 });

    // Aktuellen Canvas-Zustand als canvas_json mitschicken (Backup fuer views-Ausfall).
    let canvasJson = null;
    try {
      canvasJson = cm.canvas.toJSON([
        'customName', 'isLocked', 'isGrid', 'isBleed', 'isHem', 'isHohlsaum',
        'isGrommet', 'isGuide', 'guideOrientation', 'guidePositionPx',
        'excludeFromExport', '_curvedText', '_curvedType', '_curvedFontSize',
        '_curvedFontFamily', '_curvedFontWeight', '_curvedFill', '_curvedStroke',
        '_curvedStrokeWidth', '_curvedRadiusFactor', '_curvedSpacing',
      ]);
    } catch (e) {
      console.warn('canvas.toJSON failed:', e);
    }

    const payload = {
      name: productName || 'Unbenanntes Design',
      width_cm: widthCm,
      height_cm: heightCm,
      canvas_json: canvasJson,
      views: allViews,
      preview_image: preview,
      finishing: finishing,
      bleed_cm: bleedCm,
      hem_cm: hemCm,
      grommets: grommets,
      hohlsaum: hohlsaum,
      customer_email: customerEmail || undefined,
      customer_name: customerName || undefined,
      order_id: orderNumber || undefined,
      shop_type: shopType || 'manual',
      is_customer: isCustomerDesign,
    };

    // Include original print file if uploaded
    if (cm._printFileOriginal) {
      payload.print_file = cm._printFileOriginal;
      payload.print_file_name = cm._printFileName || 'druckdatei';
      payload.print_file_type = cm._printFileType || 'application/pdf';
    }

    // Admin-Credentials mitschicken falls eingeloggt
    if (sessionStorage.getItem('selenny-admin')) {
      payload._user = sessionStorage.getItem('selenny-admin-user') || 'admin';
      payload._pass = sessionStorage.getItem('selenny-admin-pass') || '';
    }

    try {
      const asNew = options && options.asNew === true;
      const UUID_RE = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
      const hasValidId = !!designId && UUID_RE.test(String(designId));
      let method = (hasValidId && !asNew) ? 'PUT' : 'POST';
      let url = (hasValidId && !asNew) ? `${API_BASE}/designs/${designId}` : `${API_BASE}/designs`;
      let res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      // PUT auf fremde/fehlende ID → POST (neu anlegen) als Fallback
      if (!res.ok && method === 'PUT' && (res.status === 404 || res.status === 400)) {
        console.warn('PUT /designs/' + designId + ' returned ' + res.status + ' — falling back to POST (create new)');
        method = 'POST';
        url = `${API_BASE}/designs`;
        res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        });
      }
      if (!res.ok) {
        const errText = await res.text().catch(() => '');
        throw new Error('HTTP ' + res.status + (errText ? (': ' + errText.slice(0, 200)) : ''));
      }
      const data = await res.json();
      if (!data || !data.id) {
        throw new Error('Backend hat keine Design-ID zurückgegeben');
      }
      setDesignId(data.id);
      toast.success(asNew ? 'Als neues Design gespeichert!' : 'Design erfolgreich gespeichert!');
      speech.designSaved();
      // Autosave-Backup löschen — explizit gespeichert, keine Recovery mehr nötig
      try {
        clearAutosaveStorage(designId);
        clearAutosaveStorage(data.id);
      } catch {}
      return data.id;
    } catch (err) {
      console.error('Speichern fehlgeschlagen:', err);
      toast.error('Fehler beim Speichern: ' + err.message);
      speech.error('Speichern fehlgeschlagen');
      return null;
    }
  }, [designId, widthCm, heightCm, productName, finishing, bleedCm, hemCm, grommets, hohlsaum]);

  // ── Autosave: speichert Canvas-JSON throttled in localStorage ─────────────
  // Schützt User vor Datenverlust durch Canvas-Bugs (doppelklick/text-move).
  useAutosave({
    canvasManagerRef,
    viewManagerRef,
    isInitialized,
    designId,
    meta: { widthCm, heightCm, productName, finishing, bleedCm, hemCm, grommets, hohlsaum },
  });

  // ── Recovery: prüft beim Mount ob neuere Autosave-Daten existieren ────────
  useEffect(() => {
    if (!isInitialized) return;
    if (recoveryCheckedRef.current) return;

    // Nur nach kurzer Verzögerung prüfen, damit der DB-Load Chance hatte zu starten
    const timer = setTimeout(() => {
      recoveryCheckedRef.current = true;
      // Recovery-Modal DEAKTIVIERT. Auto-Save läuft weiter (für späteres Restore via API),
      // aber kein Modal mehr beim Aufruf — verwirrte normale Kunden.
      try {
        // Alle alten Autosave-Einträge aus localStorage räumen
        for (let i = localStorage.length - 1; i >= 0; i--) {
          const k = localStorage.key(i);
          if (k && k.startsWith('selenny-designer-autosave')) {
            localStorage.removeItem(k);
          }
        }
      } catch (e) {}
      return;
    }, 1500);
    return () => clearTimeout(timer);
  }, [isInitialized]);

  const handleRecoveryAccept = useCallback(async () => {
    const cm = canvasManagerRef.current;
    const vm = viewManagerRef.current;
    if (!cm || !recoveryData) { setRecoveryData(null); return; }
    try {
      // Meta wiederherstellen
      if (recoveryData.widthCm && recoveryData.heightCm) {
        setWidthCm(recoveryData.widthCm);
        setHeightCm(recoveryData.heightCm);
        cm.setDimensions(recoveryData.widthCm, recoveryData.heightCm);
      }
      if (recoveryData.productName) setProductName(recoveryData.productName);
      if (recoveryData.finishing) setFinishing(recoveryData.finishing);
      if (recoveryData.bleedCm != null) setBleedCm(recoveryData.bleedCm);
      if (recoveryData.hemCm != null) setHemCm(recoveryData.hemCm);
      if (recoveryData.grommets) setGrommets(recoveryData.grommets);
      if (recoveryData.hohlsaum) setHohlsaum(recoveryData.hohlsaum);

      // Views oder Single-Canvas laden
      if (recoveryData.views && Array.isArray(recoveryData.views) && recoveryData.views.length > 0 && vm) {
        await vm.loadAllViews(recoveryData.views);
      } else if (recoveryData.json) {
        await cm.loadJSON(recoveryData.json);
      }
      toast.success('Ungespeicherte Änderungen wiederhergestellt');
    } catch (e) {
      console.error('[recovery] restore failed:', e);
      toast.error('Wiederherstellung fehlgeschlagen');
    } finally {
      setRecoveryData(null);
    }
  }, [recoveryData]);

  const handleRecoveryDismiss = useCallback(() => {
    const params = new URLSearchParams(window.location.search);
    const urlDesignId = params.get('design_id') || null;
    clearAutosaveStorage(urlDesignId);
    setRecoveryData(null);
  }, []);

  // Load a design by ID (used by MyDesignsPanel)
  const handleDesignLoad = useCallback((id) => {
    const url = new URL(window.location.href);
    url.searchParams.set('design_id', id);
    // Im embedded-Modus: replace statt assign, damit kein iframe-History-Eintrag entsteht
    // (Firefox Android feuert sonst popstate im Parent bei Back)
    if (isEmbedded) {
      window.location.replace(url.toString());
    } else {
      window.location.href = url.toString();
    }
  }, [isEmbedded]);

  const handleAddToCart = useCallback(async (pricingData) => {
    const cm = canvasManagerRef.current;
    setExportLoading(true);
    // Immer speichern (auch wenn schon gespeichert — aktualisiert den Stand)
    const savedId = await handleSaveDesign();

    // Wenn das Speichern keine gültige UUID lieferte, brechen wir den Cart-Add ab —
    // sonst landet das Design ohne Backend-Eintrag im Warenkorb und ist später nicht
    // mehr im Designer-Backend auffindbar (typischer "SKU-statt-UUID"-Fehler).
    const UUID_RE = /^[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}$/i;
    if (!savedId || !UUID_RE.test(String(savedId))) {
      setExportLoading(false);
      toast.error('Design konnte nicht gespeichert werden — bitte erneut versuchen.');
      return;
    }

    if (isEmbedded && cm?.canvas) {
      // Preview als JPEG für den Warenkorb
      let preview = null;
      try {
        cm.canvas.renderAll();
        preview = cm.canvas.toDataURL({ format: 'jpeg', quality: 0.6, multiplier: 0.35 });
      } catch (e) {
        console.warn('Canvas toDataURL failed (CORS?), trying background image fallback', e);
      }
      // Fallback: gespeichertes Hintergrundbild verwenden (z.B. bei eigener Druckdatei)
      if (!preview || preview === 'data:,') {
        try {
          if (cm._bgImageDataUrl) {
            preview = cm._bgImageDataUrl;
          } else if (cm._printFileOriginal && cm._printFileType?.startsWith('image/')) {
            preview = cm._printFileOriginal;
          }
        } catch {}
      }

      // An den Shop senden — mit Preisdaten aus dem ProductPanel
      window.parent.postMessage({
        type: 'designer-add-to-cart',
        designId: savedId,
        preview,
        pricing: pricingData || null,
        finishing: finishing || null,
        bleed_cm: bleedCm || 0,
        hem_cm: hemCm || 0,
        grommets: grommets || null,
        hohlsaum: hohlsaum || null,
        width_cm: widthCm,
        height_cm: heightCm,
      }, '*');
      toast.success('Design wurde in den Warenkorb gelegt!');

      // Druckdaten automatisch im Hintergrund generieren
      const dId = savedId || designId;
      if (dId && cm?.canvas) {
        try {
          const bleed = cm.bleedCm || 1;
          const w = cm.widthCm || widthCm;
          const h = cm.heightCm || heightCm;
          let svgData = null;
          try { svgData = cm.toExportSVG(); } catch (e) {}
          const _exportDpi = (() => {
            if (printDpi && printDpi >= 72 && printDpi <= 1200) return printDpi;
            const p = new URLSearchParams(window.location.search);
            const d = parseInt(p.get('designer_dpi') || p.get('dpi') || '144', 10);
            return (d >= 72 && d <= 1200) ? d : 144;
          })();
          let pngData = null;
          try { pngData = await cm.toExportWithBleed({ dpi: _exportDpi }); } catch (e) {}

          const userObjs = cm.canvas.getObjects().filter(
            (o) => !o.excludeFromExport && !o.isGrid && !o.isBleed && !o.isHem &&
                   !o.isHohlsaum && !o.isGrommet && !o.isSnapLine && !o.isGuide
          );
          const svgLooksEmpty = !svgData || /<svg[^>]*>\s*<desc>[^<]*<\/desc>\s*<rect[^\/]*\/>\s*<\/svg>/i.test(svgData);
          if (userObjs.length > 0 && svgLooksEmpty) {
            toast.error('Druckdaten-Export fehlgeschlagen — bitte Support kontaktieren!');
            try {
              fetch(`${API_BASE}/export-error`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  designId: dId, orderId: orderNumber || null,
                  objects: userObjs.length, svgLen: (svgData||'').length,
                  pngLen: (pngData||'').length,
                }),
              }).catch(() => {});
            } catch {}
          }

          if (svgData || (pngData && pngData.length > 100)) {
            const exportPayload = {
              svg_data: svgData || null,
              png_data: pngData || null,
              dpi: _exportDpi,
              width_cm: w, height_cm: h, bleed_cm: bleed,
              total_width_cm: w + 2 * bleed, total_height_cm: h + 2 * bleed,
              internal_scale: cm.internalScale || 1,
            };
            if (cm._printFileOriginal) {
              exportPayload.print_file = cm._printFileOriginal;
              exportPayload.print_file_name = cm._printFileName || 'druckdatei';
            }
            fetch(`${API_BASE}/export/${dId}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(exportPayload),
            }).catch(() => {});
          }
        } catch (e) { console.warn('Auto-export failed:', e); }
      }
    } else if (cm?.canvas) {
      // Standalone-Modus: Druckdatei exportieren
      const dId = savedId || designId;
      if (!dId) {
        toast.error('Design muss zuerst gespeichert werden.');
        setExportLoading(false);
        return;
      }
      try {
        toast.info('Druckdatei wird generiert...');
        const bleed = cm.bleedCm || 1;
        const w = cm.widthCm || widthCm;
        const h = cm.heightCm || heightCm;

        let svgData = null;
        try { svgData = cm.toExportSVG(); } catch (e) { console.warn('SVG export failed:', e); }

        let pngData = null;
        const _exportDpi = (() => {
          if (printDpi && printDpi >= 72 && printDpi <= 1200) return printDpi;
          const p = new URLSearchParams(window.location.search);
          const d = parseInt(p.get('designer_dpi') || p.get('dpi') || '144', 10);
          return (d >= 72 && d <= 1200) ? d : 144;
        })();
        try { pngData = await cm.toExportWithBleed({ dpi: _exportDpi }); } catch (e) { console.warn('PNG export failed:', e); }

        if (svgData || (pngData && pngData.length > 100)) {
          const cartExportPayload = {
              svg_data: svgData || null,
              png_data: pngData || null,
              dpi: _exportDpi,
              width_cm: w,
              height_cm: h,
              bleed_cm: bleed,
              total_width_cm: w + 2 * bleed,
              total_height_cm: h + 2 * bleed,
              internal_scale: cm.internalScale || 1,
          };
          if (cm._printFileOriginal) {
            cartExportPayload.print_file = cm._printFileOriginal;
            cartExportPayload.print_file_name = cm._printFileName || 'druckdatei';
          }
          if (sessionStorage.getItem('selenny-admin')) {
            cartExportPayload._user = sessionStorage.getItem('selenny-admin-user') || 'admin';
            cartExportPayload._pass = sessionStorage.getItem('selenny-admin-pass') || '';
          }
          const resp = await fetch(`${API_BASE}/export/${dId}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(cartExportPayload),
          });
          const result = await resp.json();
          if (resp.ok) {
            toast.success('Druckdatei wird heruntergeladen...');
            // Download ausloesen
            const link = document.createElement('a');
            const adminUser = sessionStorage.getItem('selenny-admin-user') || 'admin';
            const adminPass = sessionStorage.getItem('selenny-admin-pass') || '';
            const authQuery = sessionStorage.getItem('selenny-admin') ? `?_user=${encodeURIComponent(adminUser)}&_pass=${encodeURIComponent(adminPass)}` : '';
            link.href = `${API_BASE}/designs/${dId}/export${authQuery}`;
            link.download = `Druckdatei_${w}x${h}cm.svg`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
          } else {
            toast.error('Export fehlgeschlagen: ' + (result.error || 'Unbekannter Fehler'));
          }
        } else {
          toast.error('Keine Exportdaten erzeugt.');
        }
      } catch (err) {
        console.error('Export failed:', err);
        toast.error('Export fehlgeschlagen: ' + err.message);
      }
    }
    setExportLoading(false);
  }, [designId, handleSaveDesign, isEmbedded, widthCm, heightCm]);

  // Keep ref in sync so postMessage handler can call it
  handleAddToCartRef.current = handleAddToCart;

  const toggleSpeech = useCallback(() => {
    const newVal = speech.toggle();
    setSpeechEnabled(newVal);
    setShowSpeechHint(false);
    try { localStorage.setItem('selenny-speech-hint-dismissed', '1'); } catch (e) {}
    toast.info(newVal ? 'Sprachausgabe aktiviert' : 'Sprachausgabe deaktiviert');
  }, []);

  // Sprachausgabe-Hinweis einmalig zeigen (Bug 4): Wenn Speech aus + Hinweis noch nicht gesehen
  useEffect(() => {
    if (isEmbedded) return; // im Embedded-Mode keinen Hinweis zeigen
    if (speechEnabled) return;
    try {
      if (localStorage.getItem('selenny-speech-hint-dismissed') === '1') return;
    } catch (e) {}
    // Erst nach 1.5s damit nicht beim Splash sofort erscheint
    const t = setTimeout(() => setShowSpeechHint(true), 1500);
    return () => clearTimeout(t);
  }, [isEmbedded, speechEnabled]);

  // --- Copy current design 1:1 as a new template (admin only) ---
  // Original customer design stays untouched, a new template row is created.
  // Opens modal (window.prompt is blocked in iframes / embedded-Mode).
  const handleCopyAsTemplate = useCallback(() => {
    if (!designId) { toast.error('Kein Design geladen'); return; }
    setCopyTemplateName(productName || '');
    setCopyTemplateCategory('');
    setShowCopyTemplateModal(true);
  }, [designId, productName]);

  const submitCopyAsTemplate = useCallback(async () => {
    if (!designId) { toast.error('Kein Design geladen'); return; }
    const name = (copyTemplateName || '').trim();
    if (!name) { toast.error('Bitte einen Namen eingeben'); return; }
    const category = (copyTemplateCategory || '').trim();
    setCopyTemplateLoading(true);
    try {
      const authUser = sessionStorage.getItem('selenny-admin-user') || 'admin';
      const authPass = sessionStorage.getItem('selenny-admin-pass') || '';
      const url = `${API_BASE}/designs/${designId}/copy-as-template?_user=${encodeURIComponent(authUser)}&_pass=${encodeURIComponent(authPass)}`;
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, template_category: category }),
      });
      const data = await res.json();
      if (res.ok && data.id) {
        toast.success('Als neue Vorlage gespeichert. Original bleibt unveraendert.');
        setShowCopyTemplateModal(false);
      } else {
        toast.error('Fehler: ' + (data.error || 'unbekannt'));
      }
    } catch (e) {
      toast.error('Netzwerkfehler: ' + e.message);
    } finally {
      setCopyTemplateLoading(false);
    }
  }, [designId, copyTemplateName, copyTemplateCategory]);

  // --- Share Design ---
  const handleShareDesign = useCallback(async () => {
    try {
      // Save first if needed
      let currentId = designId;
      if (!currentId) {
        currentId = await handleSaveDesign();
      }
      if (!currentId) {
        toast.error('Design muss zuerst gespeichert werden');
        return;
      }
      const res = await fetch(`${API_BASE}/designs/${currentId}/share`, { method: 'POST' });
      const data = await res.json();
      if (data.share_url) {
        setShareUrl(data.share_url);
        setShowShareModal(true);
      } else {
        toast.error('Fehler beim Erstellen des Share-Links');
      }
    } catch (err) {
      console.error('Share failed:', err);
      toast.error('Teilen fehlgeschlagen: ' + err.message);
    }
  }, [designId, handleSaveDesign]);

  // Clone a shared design into own new design
  const handleCloneSharedDesign = useCallback(async () => {
    const params = new URLSearchParams(window.location.search);
    const sharedToken = params.get('shared');
    if (!sharedToken) return;

    try {
      // Load the shared design data
      const res = await fetch(`${API_BASE}/shared/${sharedToken}`);
      if (!res.ok) { toast.error('Design nicht gefunden'); return; }
      const data = await res.json();

      // Create a new design based on it
      const cm = canvasManagerRef.current;
      const vm = viewManagerRef.current;

      const payload = {
        name: (data.name || 'Unbenanntes Design') + ' (Kopie)',
        width_cm: data.width_cm,
        height_cm: data.height_cm,
        views: data.views || undefined,
        canvas_json: data.canvas_json || undefined,
        preview_image: data.preview_image || undefined,
        finishing: data.finishing || undefined,
        bleed_cm: data.bleed_cm || undefined,
        hem_cm: data.hem_cm || undefined,
        grommets: data.grommets || undefined,
        hohlsaum: data.hohlsaum || undefined,
        customer_email: customerEmail || undefined,
        is_customer: isCustomerDesign,
      };

      const saveRes = await fetch(`${API_BASE}/designs`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const saveData = await saveRes.json();

      if (saveData.id) {
        // Redirect to the new design (replace im embedded-Modus → kein History-Eintrag)
        const newUrl = new URL(window.location.href);
        newUrl.searchParams.delete('shared');
        newUrl.searchParams.set('design_id', saveData.id);
        if (isEmbedded) {
          window.location.replace(newUrl.toString());
        } else {
          window.location.href = newUrl.toString();
        }
      } else {
        toast.error('Fehler beim Übernehmen des Designs');
      }
    } catch (err) {
      console.error('Clone shared failed:', err);
      toast.error('Übernehmen fehlgeschlagen: ' + err.message);
    }
  }, [customerEmail]);

  return (
    <div className="h-screen flex flex-col overflow-hidden">
      <ToastContainer />
      <WarningToast />
      <AssistantPanel whatsappNumber={whatsappNumber} whatsappText={whatsappText} canvasManager={canvasManagerRef.current} />
      <AssistantOverlay />

      {/* Design Loading Overlay */}
      {designLoading && (
        <div className="fixed inset-0 z-[700] flex items-center justify-center bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800">
          <div className="text-center px-8 max-w-sm">
            {/* Animated icon */}
            <div className="mb-6" style={{ animation: 'welcome-title 0.6s cubic-bezier(0.16,1,0.3,1) both' }}>
              <svg className="w-16 h-16 mx-auto text-white/80" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round">
                <rect x="3" y="3" width="18" height="18" rx="2"/>
                <path d="M9 3v18M3 9h18"/>
              </svg>
            </div>
            {/* Status text */}
            <p className="text-white text-lg font-semibold mb-2" style={{ animation: 'welcome-title 0.6s cubic-bezier(0.16,1,0.3,1) 0.1s both' }}>
              {designLoadStatus}
            </p>
            <p className="text-white/50 text-sm mb-6" style={{ animation: 'welcome-title 0.6s cubic-bezier(0.16,1,0.3,1) 0.2s both' }}>
              {designLoadProgress < 100 ? 'Bitte warten...' : ''}
            </p>
            {/* Progress bar */}
            <div className="w-full h-2 rounded-full bg-white/15 overflow-hidden" style={{ animation: 'welcome-title 0.6s cubic-bezier(0.16,1,0.3,1) 0.3s both' }}>
              <div
                className="h-full rounded-full transition-all duration-500 ease-out"
                style={{ width: `${designLoadProgress}%`, background: 'linear-gradient(90deg, #60a5fa, #a78bfa, #60a5fa)', backgroundSize: '200% 100%', animation: 'shimmer 2s linear infinite' }}
              />
            </div>
            <p className="text-white/40 text-xs mt-3">{designLoadProgress}%</p>
          </div>
          <style>{`
            @keyframes shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
          `}</style>
        </div>
      )}

      {/* Admin Login Dialog */}
      {showAdminLogin && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50 backdrop-blur-sm" onClick={() => setShowAdminLogin(false)}>
          <div className="bg-white rounded-2xl p-6 shadow-2xl max-w-xs mx-4 w-full" onClick={e => e.stopPropagation()}>
            <h3 className="text-lg font-bold text-gray-800 mb-4">Admin Login</h3>
            <form onSubmit={async (e) => {
              e.preventDefault();
              const form = e.target;
              const ok = await handleAdminLogin(form.user.value, form.pass.value);
              if (!ok) { form.pass.value = ''; alert('Login fehlgeschlagen'); }
            }}>
              <input name="user" type="text" placeholder="Benutzer" defaultValue="admin" className="w-full mb-3 px-4 py-3 border border-gray-300 rounded-xl text-base" />
              <input name="pass" type="password" placeholder="Passwort" className="w-full mb-4 px-4 py-3 border border-gray-300 rounded-xl text-base" autoFocus />
              <div className="flex gap-3">
                <button type="button" onClick={() => setShowAdminLogin(false)} className="flex-1 py-3 rounded-xl border border-gray-300 text-gray-600 font-medium">Abbrechen</button>
                <button type="submit" className="flex-1 py-3 rounded-xl bg-blue-600 text-white font-bold">Anmelden</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Embedded loading screen */}
      {embeddedLoading && (
        <EmbeddedLoader onReady={() => setEmbeddedLoading(false)} />
      )}

      {/* Welcome Splash - then onboarding guide */}
      {showWelcome && (
        <WelcomeSplash onDone={() => { setShowWelcome(false); setShowOnboarding(true); }} />
      )}
      {showOnboarding && !showWelcome && (
        <OnboardingGuide onDone={() => setShowOnboarding(false)} />
      )}
      {exportLoading && (
        <div className="fixed inset-0 z-[400] flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl p-8 shadow-2xl flex flex-col items-center gap-4 max-w-xs mx-4">
            <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin" />
            <p className="text-base font-semibold text-gray-800">Druckdaten werden erstellt...</p>
            <p className="text-sm text-gray-500 text-center">Design wird gespeichert und in Druckqualit&auml;t exportiert. Bitte warten.</p>
          </div>
        </div>
      )}
      {previewMode && (
        <PreviewOverlay
          canvasManager={canvasManagerRef.current}
          widthCm={widthCm}
          heightCm={heightCm}
          finishing={finishing}
          hemPerSide={hemPerSide}
          grommets={grommets}
          hohlsaum={hohlsaum}
          bleedCm={bleedCm}
          onClose={() => setPreviewMode(false)}
        />
      )}

      {!isEmbedded && !isMobile && <Header speechEnabled={speechEnabled} onToggleSpeech={toggleSpeech} darkMode={darkMode} onToggleDark={() => setDarkMode(d => !d)} isAdmin={isAdmin} onAdminLogin={() => setShowAdminLogin(true)} onAdminLogout={() => { sessionStorage.removeItem('selenny-admin'); sessionStorage.removeItem('selenny-admin-user'); sessionStorage.removeItem('selenny-admin-pass'); setIsAdmin(false); }} />}
      {!isMobile && <TopToolbar
        canvasManager={canvasManagerRef.current}
        historyManager={historyManagerRef.current}
        gridManager={gridManagerRef.current}
        activeObject={activeObject}
        canUndo={canUndo}
        canRedo={canRedo}
        gridVisible={gridVisible}
        previewMode={previewMode}
        onTogglePreview={() => { setPreviewMode(true); }}
        showRulers={showRulers}
        onToggleRulers={() => { const next = !showRulers; setShowRulers(next); speech.rulersToggled(next); }}
        isEmbedded={isEmbedded}
        darkMode={darkMode}
        onToggleDark={() => setDarkMode(d => !d)}
        speechEnabled={speechEnabled}
        onToggleSpeech={toggleSpeech}
        onShare={handleShareDesign}
        isSharedView={isSharedView}
        isAdmin={isAdmin}
        designId={designId}
        onCopyAsTemplate={handleCopyAsTemplate}
      />}
      {isMobile && (
        <MobileTopBar
          canUndo={canUndo}
          canRedo={canRedo}
          onUndo={() => historyManagerRef.current?.undo()}
          onRedo={() => historyManagerRef.current?.redo()}
          onDelete={() => canvasManagerRef.current?.deleteSelected()}
          onSave={handleSaveDesign}
          activeObject={activeObject}
          productName={productName}
          darkMode={darkMode}
          isEmbedded={isEmbedded}
        />
      )}
      {/* Shared Design Banner */}
      {isSharedView && (
        <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700 dark:text-blue-200 text-sm z-50">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
            <span>
              <strong>Geteiltes Design</strong> von <strong>{sharedDesignName}</strong> — Nur Vorschau
            </span>
          </div>
          <button
            onClick={handleCloneSharedDesign}
            className="ml-4 px-4 py-1.5 bg-blue-600 text-white text-xs font-bold rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-1.5"
          >
            <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="8" y="8" width="12" height="12" rx="1"/><path d="M4 16V5a1 1 0 0 1 1-1h11"/></svg>
            Design übernehmen
          </button>
        </div>
      )}

      {/* Share Modal */}
      {showShareModal && (
        <div className="fixed inset-0 z-[600] flex items-center justify-center bg-black/50" onClick={() => setShowShareModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl p-6 max-w-md w-full mx-4" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="18" cy="5" r="3"/><circle cx="6" cy="12" r="3"/><circle cx="18" cy="19" r="3"/><line x1="8.59" y1="13.51" x2="15.42" y2="17.49"/><line x1="15.41" y1="6.51" x2="8.59" y2="10.49"/></svg>
                Design teilen
              </h3>
              <button onClick={() => setShowShareModal(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors">
                <svg className="w-5 h-5 text-gray-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M6 18L18 6M6 6l12 12"/></svg>
              </button>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              Jeder mit diesem Link kann dein Design ansehen (nicht bearbeiten).
            </p>
            <div className="flex items-center gap-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 px-3 py-2 text-sm bg-gray-100 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg text-gray-800 dark:text-gray-200 select-all"
                onClick={e => e.target.select()}
              />
              <button
                onClick={() => {
                  navigator.clipboard.writeText(shareUrl).then(() => {
                    toast.success('Link kopiert!');
                  }).catch(() => {
                    const input = document.createElement('textarea');
                    input.value = shareUrl;
                    document.body.appendChild(input);
                    input.select();
                    document.execCommand('copy');
                    document.body.removeChild(input);
                    toast.success('Link kopiert!');
                  });
                }}
                className="px-4 py-2 bg-blue-600 text-white text-sm font-bold rounded-lg hover:bg-blue-700 transition-colors whitespace-nowrap"
              >
                Link kopieren
              </button>
            </div>
          </div>
        </div>
      )}

      {showExpiryWarning && expiresInDays != null && (
        <div className="flex items-center justify-between px-4 py-2 bg-amber-50 border-b border-amber-200 text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200 text-sm z-50">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-amber-500 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4.5c-.77-.833-2.694-.833-3.464 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span>
              <strong>Hinweis:</strong> Dieses Design wird in <strong>{expiresInDays}</strong> {expiresInDays === 1 ? 'Tag' : 'Tagen'} automatisch gel&ouml;scht, wenn du es nicht bearbeitest oder etwas bestellst.
            </span>
          </div>
          <button
            onClick={() => setShowExpiryWarning(false)}
            className="ml-4 p-1 rounded-full hover:bg-amber-200/60 transition-colors flex-shrink-0"
            title="Schliessen"
          >
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      {isMobile ? (
        // ─── Mobile Layout ───
        <div className="flex flex-col flex-1 overflow-hidden relative" style={{ paddingBottom: isEmbedded ? '92px' : '54px' }}>
          <CanvasArea
            zoom={zoom}
            onZoomChange={handleZoomChange}
            previewMode={previewMode}
            widthCm={widthCm}
            heightCm={heightCm}
            bleedCm={bleedCm}
            hemCm={hemCm}
            hemPerSide={hemPerSide}
            hohlsaum={hohlsaum}
            overlayColors={overlayColors}
            canvasManager={canvasManagerRef.current}
            showRulers={false}
            gridVisible={false}
          />

          {/* Mobile Property Sheet (when object selected) */}
          <MobilePropertySheet
            isOpen={!!activeObject && !mobileTab}
            onClose={() => { canvasManagerRef.current?.canvas?.discardActiveObject(); canvasManagerRef.current?.canvas?.renderAll(); setActiveObject(null); }}
            activeObject={activeObject}
            canvasManager={canvasManagerRef.current}
            bgRemoval={bgRemovalRef.current}
          />

          {/* Mobile Bottom Sheets */}
          <MobileSizeSheet
            isOpen={mobileTab === 'size'}
            onClose={() => setMobileTab(null)}
            widthCm={widthCm}
            heightCm={heightCm}
            onDimensionChange={handleDimensionChange}
            activeSizes={urlPricing?.standardSizes?.length > 0 ? urlPricing.standardSizes.map(s => { const w = Number(s.w || s.width || 0); const h = Number(s.h || s.height || 0); return { label: s.label || `${w}\u00D7${h}cm`, w, h, fixedPrice: s.fixedPrice || s.fixed_price || null }; }) : []}
            hideSizes={hideSizes}
          />
          <MobileShapesSheet isOpen={mobileTab === 'shapes'} onClose={() => setMobileTab(null)} canvasManager={canvasManagerRef.current} />
          <MobileTextSheet isOpen={mobileTab === 'text'} onClose={() => setMobileTab(null)} canvasManager={canvasManagerRef.current} />
          <MobileImagesSheet isOpen={mobileTab === 'images'} onClose={() => setMobileTab(null)} canvasManager={canvasManagerRef.current} />
          <MobileLayersSheet
            isOpen={mobileTab === 'layers'}
            onClose={() => setMobileTab(null)}
            layers={layers}
            layerManager={layerManagerRef.current}
            canvasManager={canvasManagerRef.current}
            activeObject={activeObject}
          />
          <MobileMoreSheet
            isOpen={mobileTab === 'more'}
            onClose={() => setMobileTab(null)}
            canvasManager={canvasManagerRef.current}
            layers={layers}
            activeObject={activeObject}
          />

          {/* Product Panel as sheet */}
          <MobileBottomSheet isOpen={mobileTab === 'product'} onClose={() => setMobileTab(null)} title="Produkt" height="50vh">
            <ProductPanel
              widthCm={widthCm}
              heightCm={heightCm}
              productName={productName}
              onDimensionChange={handleDimensionChange}
              onProductNameChange={setProductName}
              onSave={handleSaveDesign}
              onSaveAsNew={() => handleSaveDesign({ asNew: true })}
              canSave={isAdmin || isLoggedIn}
              isAdmin={isAdmin}
              onAddToCart={handleAddToCart}
              onPricingChange={pricingRef}
              designId={designId}
              isEmbedded={isEmbedded}
              hideSizes={hideSizes}
              bleedCm={bleedCm}
              hemCm={hemCm}
              grommets={grommets}
              onBleedChange={handleBleedChange}
              onHemChange={handleHemChange}
              onGrommetsChange={handleGrommetsChange}
              hohlsaum={hohlsaum}
              onHohlsaumChange={handleHohlsaumChange}
              finishing={finishing}
              onFinishingChange={handleFinishingChange}
              productId={productId}
              shopUrl={shopUrl}
              urlPricing={urlPricing}
            />
          </MobileBottomSheet>

          {/* Meine Designs as sheet */}
          <MobileBottomSheet isOpen={mobileTab === 'designs'} onClose={() => setMobileTab(null)} title="Meine Designs" height="70vh">
            <MyDesignsPanel
              customerEmail={customerEmail}
              canvasManager={canvasManagerRef.current}
              designId={designId}
              onDesignLoad={handleDesignLoad}
              onClose={() => setMobileTab(null)}
              inline={true}
            />
          </MobileBottomSheet>

          {/* Bottom Tab Bar + Warenkorb */}
          <MobileBottomTabBar activeTab={mobileTab} onTabChange={setMobileTab}
            cartButton={isEmbedded && !mobileTab ? (
              <button
                data-guide-id="btn-cart"
                onClick={() => handleAddToCart(pricingRef.current)}
                style={{
                  width: '100%', padding: '14px', background: '#16a34a', color: '#fff',
                  border: 'none', borderRadius: '12px', fontSize: '15px', fontWeight: 700,
                  cursor: 'pointer', letterSpacing: '0.5px',
                  WebkitTapHighlightColor: 'transparent', touchAction: 'manipulation',
                }}
              >
                IN DEN WARENKORB
              </button>
            ) : null}
          />
        </div>
      ) : (
        // ─── Desktop Layout (unchanged) ───
        <div className="flex flex-1 overflow-hidden">
          <LeftToolbar
            canvasManager={canvasManagerRef.current}
            imageFilters={imageFiltersRef.current}
            activeObject={activeObject}
            layerManager={layerManagerRef.current}
            layers={layers}
            customerEmail={customerEmail}
            designId={designId}
            onDesignLoad={handleDesignLoad}
            allowedClipartCategories={allowedClipartCategories}
          />
          <div className="flex flex-col flex-1 min-w-0 overflow-hidden relative">
            {/* Context hints - floating overlay, doesn't affect layout */}
            <ContextHints activeObject={activeObject} canvasManager={canvasManagerRef.current} />
            <CanvasArea
              zoom={zoom}
              onZoomChange={handleZoomChange}
              previewMode={previewMode}
              widthCm={widthCm}
              heightCm={heightCm}
              bleedCm={bleedCm}
              hemCm={hemCm}
              hemPerSide={hemPerSide}
              hohlsaum={hohlsaum}
              overlayColors={overlayColors}
              canvasManager={canvasManagerRef.current}
              showRulers={showRulers}
              gridVisible={gridVisible}
            />
            <ViewTabs
              viewManager={viewManagerRef.current}
              views={views}
              activeIndex={activeViewIndex}
            />
          </div>
          <div className={`w-72 border-l border-gray-200 bg-white flex flex-col overflow-hidden pr-2.5 transition-opacity duration-300 ${isInitialized ? 'opacity-100' : 'opacity-0'}`}>
            <ProductPanel
              widthCm={widthCm}
              heightCm={heightCm}
              productName={productName}
              onDimensionChange={handleDimensionChange}
              onProductNameChange={setProductName}
              onSave={handleSaveDesign}
              onSaveAsNew={() => handleSaveDesign({ asNew: true })}
              canSave={isAdmin || isLoggedIn}
              isAdmin={isAdmin}
              onAddToCart={handleAddToCart}
              onPricingChange={pricingRef}
              designId={designId}
              isEmbedded={isEmbedded}
              hideSizes={hideSizes}
              bleedCm={bleedCm}
              hemCm={hemCm}
              grommets={grommets}
              onBleedChange={handleBleedChange}
              onHemChange={handleHemChange}
              onGrommetsChange={handleGrommetsChange}
              hohlsaum={hohlsaum}
              onHohlsaumChange={handleHohlsaumChange}
              finishing={finishing}
              onFinishingChange={handleFinishingChange}
              productId={productId}
              shopUrl={shopUrl}
              urlPricing={urlPricing}
            />
            {/* PropertyPanel moved to floating overlay to avoid blocking save/cart buttons */}
          </div>
        </div>
      )}
      {/* Floating property panel - appears when object selected, draggable (desktop only) */}
      {activeObject && !isMobile && (
        <div className="fixed z-[90]" style={{ right: 8, top: 100 }}>
          <PropertyPanel
            canvasManager={canvasManagerRef.current}
            activeObject={activeObject}
            imageFilters={imageFiltersRef.current}
            bgRemoval={bgRemovalRef.current}
            onClose={() => {
              if (canvasManagerRef.current?.canvas) {
                canvasManagerRef.current.canvas.discardActiveObject();
                canvasManagerRef.current.canvas.renderAll();
              }
              setActiveObject(null);
            }}
          />
        </div>
      )}
      {/* Collage Image Uploader */}
      {activeCollage && !collageMinimized && (
        <CollageImageUploader
          canvasManager={canvasManagerRef.current}
          collageId={activeCollage.collageId}
          placeholderCount={activeCollage.placeholderCount}
          onClose={() => setCollageMinimized(true)}
          onDiscard={() => { setActiveCollage(null); setCollageMinimized(false); }}
        />
      )}
      {/* Minimized collage pill — always visible when a collage exists but is minimized */}
      {activeCollage && collageMinimized && (
        <button
          onClick={() => setCollageMinimized(false)}
          className="fixed z-50 flex items-center gap-2 px-4 py-2.5 rounded-full bg-blue-500 text-white shadow-lg hover:bg-blue-600 hover:shadow-xl transition-all hover:scale-105 active:scale-95"
          style={{ right: 310, top: 80 }}
          title="Fotos zuordnen wieder öffnen"
        >
          <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <circle cx="8.5" cy="8.5" r="2" />
            <path d="M3 16l4.5-4.5a1 1 0 011.4 0L12 14.5l2.1-2.1a1 1 0 011.4 0L21 18" />
          </svg>
          <span className="text-sm font-semibold">Fotos zuordnen</span>
          <span className="w-5 h-5 rounded-full bg-white/20 flex items-center justify-center text-[11px] font-bold">{activeCollage.placeholderCount}</span>
        </button>
      )}
      {/* Floating bg image remove pill */}
      {hasBgImage && (
        <button
          onClick={() => {
            canvasManagerRef.current?.removeBackgroundImage?.();
            setHasBgImage(false);
          }}
          className="fixed z-50 flex items-center gap-2 px-3 py-2 rounded-full bg-white text-gray-700 shadow-lg border border-gray-200 hover:border-red-300 hover:bg-red-50 hover:text-red-600 transition-all hover:shadow-xl group"
          style={{ left: 84, bottom: 20 }}
          title="Hintergrundbild entfernen und auf Weiß zurücksetzen"
        >
          <svg className="w-4 h-4 text-gray-400 group-hover:text-red-500 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <rect x="3" y="3" width="18" height="18" rx="3" />
            <path d="M3 15l5-5 4 4 3-3 6 6" />
            <circle cx="8.5" cy="8.5" r="1.5" fill="currentColor" />
          </svg>
          <span className="text-xs font-medium">Hintergrundbild entfernen</span>
          <svg className="w-3.5 h-3.5 text-gray-300 group-hover:text-red-400 transition-colors" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <path d="M18 6L6 18M6 6l12 12" />
          </svg>
        </button>
      )}
      <ZoomControl
        zoom={zoom}
        onZoomChange={handleZoomChange}
        onFitZoom={handleFitZoom}
      />

      {/* Sprachausgabe-Hinweis (Bug 4): einmalig zeigen, wenn Speech aus ist */}
      {showSpeechHint && (
        <div
          className="fixed z-[150] flex items-center gap-3 px-4 py-3 rounded-xl shadow-2xl bg-white dark:bg-gray-800 border border-blue-200 dark:border-blue-700"
          style={{ top: 56, right: 16, maxWidth: 360 }}
        >
          <svg className="w-5 h-5 text-blue-500 flex-shrink-0" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5"/>
            <line x1="23" y1="9" x2="17" y2="15"/>
            <line x1="17" y1="9" x2="23" y2="15"/>
          </svg>
          <p className="text-xs text-gray-700 dark:text-gray-200 flex-1 leading-snug">
            Sprachausgabe ist aus. Über den <strong>Ton-Button oben rechts</strong> kannst du sie aktivieren.
          </p>
          <button
            onClick={() => { setShowSpeechHint(false); try { localStorage.setItem('selenny-speech-hint-dismissed', '1'); } catch (e) {} }}
            className="w-6 h-6 flex items-center justify-center text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700 flex-shrink-0"
            aria-label="Hinweis ausblenden"
          >
            &times;
          </button>
        </div>
      )}

      {/* Text Edit Modal */}
      {editingTextObj && (
        <TextEditModal
          obj={editingTextObj}
          canvasManager={canvasManagerRef.current}
          initialText={editingTextValue}
          initialColor={editingTextColor}
          isCurved={editingTextIsCurved}
          initialFont={editingTextFont}
          initialSize={editingTextSize}
          onClose={() => setEditingTextObj(null)}
        />
      )}

      {/* Copy-as-Template Modal — ersetzt window.prompt (im iframe blockiert) */}
      {showCopyTemplateModal && (
        <div
          className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4"
          onClick={(e) => { if (e.target === e.currentTarget && !copyTemplateLoading) setShowCopyTemplateModal(false); }}
        >
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Als neue Vorlage speichern</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Das aktuelle Design wird als neue Vorlage gespeichert. Das Original bleibt unverändert.
                </p>
              </div>
            </div>
            <div className="space-y-3 mb-5">
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">Name der Vorlage *</label>
                <input
                  type="text"
                  autoFocus
                  value={copyTemplateName}
                  onChange={(e) => setCopyTemplateName(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !copyTemplateLoading) submitCopyAsTemplate(); }}
                  placeholder="z.B. Abi-Plakat 2026"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1 block">Kategorie (optional)</label>
                <input
                  type="text"
                  value={copyTemplateCategory}
                  onChange={(e) => setCopyTemplateCategory(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter' && !copyTemplateLoading) submitCopyAsTemplate(); }}
                  placeholder="z.B. abi, geburtstag"
                  className="w-full px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 focus:outline-none focus:ring-2 focus:ring-amber-500"
                />
              </div>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                onClick={() => setShowCopyTemplateModal(false)}
                disabled={copyTemplateLoading}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors disabled:opacity-50"
              >
                Abbrechen
              </button>
              <button
                onClick={submitCopyAsTemplate}
                disabled={copyTemplateLoading || !copyTemplateName.trim()}
                className="px-4 py-2 text-sm bg-amber-600 hover:bg-amber-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {copyTemplateLoading ? 'Wird gespeichert...' : 'Als Vorlage speichern'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recovery Modal — ungespeicherte Autosave-Änderungen wiederherstellen */}
      {recoveryData && (
        <div className="fixed inset-0 z-[300] flex items-center justify-center bg-black/40 backdrop-blur-sm p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-md p-6">
            <div className="flex items-start gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-amber-100 dark:bg-amber-900/40 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 text-amber-600 dark:text-amber-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M12 2v4M12 18v4M4.93 4.93l2.83 2.83M16.24 16.24l2.83 2.83M2 12h4M18 12h4M4.93 19.07l2.83-2.83M16.24 7.76l2.83-2.83" />
                </svg>
              </div>
              <div className="flex-1">
                <h3 className="text-base font-semibold text-gray-900 dark:text-gray-100">Ungespeicherte Änderungen gefunden</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                  Du hast Änderungen am Design, die nicht gespeichert wurden
                  {recoveryData.savedAt ? ` (vor ${Math.max(1, Math.round((Date.now() - recoveryData.savedAt) / 60000))} Minuten)` : ''}.
                  Möchtest du sie wiederherstellen?
                </p>
              </div>
            </div>
            <div className="flex gap-2 justify-end mt-5">
              <button
                onClick={handleRecoveryDismiss}
                className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors"
              >
                Verwerfen
              </button>
              <button
                onClick={handleRecoveryAccept}
                className="px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors font-medium"
              >
                Wiederherstellen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;
